/**
 * Murphy Door Configurator - Cart Manager Business Logic
 *
 * PURPOSE: Manages cart operations using PPD pre-built templates for O(1) lookups,
 * cart API interactions, configuration grouping, and success/error coordination.
 *
 * PHASE: 2 (Event-Driven CCP - Strict Enforcement)
 * MODULE TYPE: Business Logic (bl-)
 * GOLDEN STANDARD REFERENCE: md-config-bl-material-selector.js (200/200)
 *
 * CCP COMPLIANCE:
 * - CONSTANTS.EVENTS.* exclusively (no local EVENT_TYPES - Pattern 19)
 * - publishEvent() async helper with try/catch (Pattern 2, 11)
 * - 5-stage event lifecycle: STARTED -> VALIDATION -> PROGRESS -> COMPLETED/FAILED (Pattern 3)
 * - Event consumer handlers for bootstrap and data (Pattern 4)
 * - Event replay ready via bootstrap queue (Pattern 5)
 * - Bootstrap runtime protection via SYSTEM_STATE_FROM_PPD_READY (Pattern 6)
 * - PPD-first O(1) data access via conductor delegation (Patterns 7, 8)
 * - Data request events for product data, selections, mutations (Pattern 9)
 * - UI update events for spinner, optimistic UI, cart updates (Pattern 10)
 * - Performance tracking with thresholds (Patterns 12, 13)
 * - Try/catch + FAILED events on all operations (Pattern 14)
 * - Cleanup arrays for all subscriptions (Pattern 15)
 * - Initialization guard (Pattern 16)
 * - No user input rendered to DOM (Pattern 17 - N/A for pure BL)
 * - CoreEventBus central freeze (Pattern 18 DRY)
 * - Conductor single publication (Pattern 20)
 *
 * PERFORMANCE CHARACTERISTICS:
 * - Cart preparation: ~1-2ms (was ~50-100ms) - 50x faster!
 * - "Add to Cart" responsiveness: <10ms (was <50ms)
 * - Zero async event round-trips for product/variant lookups
 * - O(1) template reads instead of O(n) searches
 *
 * All external communication flows through md-config-core-conductor.js delegation.
 *
 * Dependencies:
 * - md-config-core-conductor.js (PPD delegation, data access)
 * - md-config-core-event-bus.js (event system)
 * - md-config-core-constants.js (centralized event constants)
 */

/** Module uses scoped pattern for Shopify compatibility */
(function (window) {
  'use strict';

  window.MurphyDoorConfigurator = window.MurphyDoorConfigurator || {};

  // Dev-only logging (production guard)
  const isDev = () =>
    window.MurphyDoorConfigurator?.utils?.isDevelopment?.() ?? false;

  // ============================================================================
  // PATTERN 1 & 19: GLOBAL CONSTANTS USAGE (10 points each)
  // No local EVENT_TYPES object - use EVENTS.* directly (DRY)
  // ============================================================================
  const CONSTANTS = window.MurphyDoorConfigurator.CONSTANTS;
  const EVENTS = CONSTANTS.EVENTS;
  const CoreEventBus = window.MurphyDoorConfigurator.CoreEventBus;

  // ============================================================================
  // Configuration constants
  // ============================================================================
  const ASYNC_REQUEST_TIMEOUT_MS = 5000;
  const BACKGROUND_ASYNC_TIMEOUT_MS = 10000;
  // Real network calls to Shopify's own cart endpoints (not internal event
  // round-trips) get their own, more generous timeout. A stalled mobile
  // connection previously left fetch() unresolved forever, so the "Adding
  // your customized door to cart..." spinner never cleared and the customer
  // never learned whether the add succeeded. See: real-device intermittent
  // ATC hang post-mortem (Ahmed QA #5, 2026-09-12).
  const CART_NETWORK_TIMEOUT_MS = 15000;
  // Re-sweeps after a failed add whose request may still commit late.
  const LATE_SWEEP_DELAYS_MS = [10000, 30000];
  // Generous over CART_NETWORK_TIMEOUT_MS so 429 retries still count as one add.
  const ADD_IN_FLIGHT_STALE_MS = 45000;
  // Stored configured-door image (Gap Plan #25b, merged door line at checkout).
  // The door PNG is uploaded BEFORE the click (prefetch, see
  // _prefetchConfiguredDoorImage) and its stored URL rides on the BASE line of
  // the /cart/add.js payload as _Configuration_Image, so the Cart Transform can
  // show the door's own picture on the merged line. Presentation only and best
  // effort: the add waits at most CONFIGURED_DOOR_IMAGE_ADD_WAIT_MS for an
  // in-flight prefetch, then proceeds without the image (the cart page /
  // drawer backfill in assets/cart.js covers that case for flat carts).
  const CONFIGURED_DOOR_SNAPSHOT_WAIT_MS = 8000;
  const CONFIGURED_DOOR_IMAGE_ADD_WAIT_MS = 1500;
  // The upload route allows 20 per 10 min per IP; uploads are already one per
  // configuration state, and this caps a long browsing session on one page.
  const CONFIGURED_DOOR_PREFETCH_MAX_UPLOADS = 8;
  const CONFIGURATION_IMAGE_PROPERTY = '_Configuration_Image';

  /**
   * Order a door's add-on lines for POST /cart/add.js so they read in the
   * configurator's card order (admin apo_load_order metafield, via the
   * conductor's _getApoOrderRank) everywhere downstream: cart, drawer, saved
   * quotes, the Draft order invoice email, checkout and the admin order.
   *
   * Shopify lists a multi-item add NEWEST-FIRST, i.e. the array reversed. That is
   * why the base line is pushed last and shows first. So the add-ons go in
   * DESCENDING rank. Ties (unknown categories rank last) keep selection order.
   *
   * @param {Array<{item: Object, category: string}>} entries - add-ons in selection order
   * @param {function(string): number} rankOf - category -> apo order rank
   * @returns {Object[]} cart items, ready to precede the base line
   */
  function orderAddOnsForCart(entries, rankOf) {
    return entries
      .map((entry, index) => {
        let rank = Number.MAX_SAFE_INTEGER;
        try {
          const r = rankOf(entry.category);
          if (Number.isFinite(r)) rank = r;
        } catch (e) {
          // Unrankable -> end of the list; ordering must never block add-to-cart.
        }
        return { item: entry.item, index, rank };
      })
      .sort((a, b) => b.rank - a.rank || b.index - a.index)
      .map((entry) => entry.item);
  }

  /**
   * Cart API Throttle Configuration
   *
   * Adds a delay between cart/add.js and cart/update.js to prevent hitting Shopify's
   * rate limits when combined with other apps/scripts hitting cart endpoints.
   *
   * Benefits:
   * - Reduces 429 rate limit errors from back-to-back cart operations
   * - Plays nice with third-party apps also using Cart API
   * - Minimal UX impact (250ms is imperceptible to users)
   */
  const CART_API_THROTTLE_MS = 250;

  /**
   * Cart API rate-limit (HTTP 429) retry policy — the ONE policy shared by
   * /cart/add.js and /cart/update.js via fetchWithRateLimitRetry() below.
   *
   * Only a 429 is retried. A 429 means Shopify throttled the request BEFORE
   * processing it, so re-sending the identical request cannot double-add a
   * line. Validation errors (422 etc.) and network failures / timeouts
   * (AbortError) are NOT retried — they surface immediately, exactly as the
   * cart-attributes path always behaved.
   *
   * History: /cart/update.js had its own inline 429 loop, but /cart/add.js had
   * none, so a single throttled add showed a real shopper "There Was a Problem
   * Adding to Cart" on a configured door. Both call sites now share this.
   */
  const CART_RATE_LIMIT_RETRY = Object.freeze({
    MAX_ATTEMPTS: 3, // total requests, including the first
    BASE_DELAY_MS: 500, // exponential backoff: 500ms, 1000ms, ...
    MAX_DELAY_MS: 5000, // hard cap on any single wait, Retry-After included
    JITTER_RATIO: 0.25, // add up to +25% so concurrent retries de-synchronise
  });

  /**
   * "Build this" attribution line properties for the BASE line, read from the
   * conductor (never from the URL). Empty object when no preset is active, so
   * an ordinary configuration carries neither key. The handle is re-checked
   * against CONSTANTS.BUILD_PRESET_HANDLE_REGEX because it lands in an order
   * and the Super App keys analytics on it. Underscore-prefixed: hidden in
   * checkout, and every cart surface escapes property values.
   * @param {Object} conductor
   * @returns {Object} {} | { _Build_Preset_ID } | { _Build_Preset_ID, _Build_Preset_Edited: 'yes' }
   */
  function getBuildPresetLineProperties(conductor) {
    try {
      if (!conductor || typeof conductor.getActiveBuildPresetId !== 'function') {
        return {};
      }
      const handle = conductor.getActiveBuildPresetId();
      const pattern = CONSTANTS.BUILD_PRESET_HANDLE_REGEX;
      if (typeof handle !== 'string' || !pattern || !pattern.test(handle)) {
        return {};
      }
      const properties = { _Build_Preset_ID: handle };
      if (
        typeof conductor.isBuildPresetEdited === 'function' &&
        conductor.isBuildPresetEdited() === true
      ) {
        properties._Build_Preset_Edited = 'yes';
      }
      return properties;
    } catch (error) {
      isDev() &&
        console.warn('[CartManager] Build preset attribution skipped:', error);
      return {};
    }
  }

  /**
   * The fallback _Configuration_URL (window.location.search) minus a
   * ?build_preset= share handle, which must never be persisted.
   * @param {string} search
   * @returns {string}
   */
  function stripBuildPresetParam(search) {
    const name = CONSTANTS.BUILD_PRESET_URL_PARAM || 'build_preset';
    if (typeof search !== 'string' || search.indexOf(name + '=') === -1) {
      return search;
    }
    try {
      const params = new URLSearchParams(search);
      params.delete(name);
      const rest = params.toString();
      return rest ? '?' + rest : '';
    } catch (error) {
      return search;
    }
  }

  /**
   * Parses a Retry-After header value (delta-seconds or HTTP-date) into ms.
   * @returns {number|null} ms to wait, or null when absent/unparseable.
   */
  function parseRetryAfterMs(headerValue, nowMs) {
    if (headerValue === null || headerValue === undefined) return null;
    const raw = String(headerValue).trim();
    if (raw === '') return null;
    if (/^\d+(\.\d+)?$/.test(raw)) return Math.round(parseFloat(raw) * 1000);
    const dateMs = Date.parse(raw);
    if (Number.isNaN(dateMs)) return null;
    return Math.max(0, dateMs - nowMs);
  }

  /**
   * Delay before retry number `retryNumber` (1-based). Never sooner than the
   * server's Retry-After nor our own backoff; jitter is additive (so it can
   * only push a retry later, never ahead of Retry-After); capped overall.
   */
  function getRateLimitRetryDelayMs(retryNumber, response) {
    const backoffMs =
      CART_RATE_LIMIT_RETRY.BASE_DELAY_MS * Math.pow(2, retryNumber - 1);
    const retryAfterMs = parseRetryAfterMs(
      response?.headers?.get?.('Retry-After'),
      Date.now()
    );
    const floorMs = Math.max(backoffMs, retryAfterMs || 0);
    const jitterMs =
      Math.random() * floorMs * CART_RATE_LIMIT_RETRY.JITTER_RATIO;
    return Math.min(
      Math.round(floorMs + jitterMs),
      CART_RATE_LIMIT_RETRY.MAX_DELAY_MS
    );
  }

  /**
   * Runs `sendOnce` (which performs exactly ONE cart request, with its own
   * per-attempt timeout, and resolves to the fetch Response) and re-runs it
   * only while Shopify answers 429, up to CART_RATE_LIMIT_RETRY.MAX_ATTEMPTS.
   *
   * Resolves with the first non-429 Response, or the final 429 Response once
   * attempts are exhausted — the caller's existing !response.ok handling then
   * reports the failure exactly as before. Rejections (network error,
   * AbortError timeout) propagate immediately and are never retried.
   *
   * Idempotency: only the throttled request is re-sent. Callers must pass a
   * closure over a single request, never a chain of requests.
   */
  async function fetchWithRateLimitRetry(sendOnce, label) {
    let response;
    for (
      let attempt = 1;
      attempt <= CART_RATE_LIMIT_RETRY.MAX_ATTEMPTS;
      attempt++
    ) {
      response = await sendOnce();
      if (
        response.status !== 429 ||
        attempt === CART_RATE_LIMIT_RETRY.MAX_ATTEMPTS
      ) {
        return response;
      }
      const delayMs = getRateLimitRetryDelayMs(attempt, response);
      isDev() &&
        console.warn(
          `[CartManager] ${label} rate limited (429) - retrying in ${delayMs}ms (attempt ${attempt + 1}/${CART_RATE_LIMIT_RETRY.MAX_ATTEMPTS})`
        );
      await new Promise((resolve) => setTimeout(resolve, delayMs));
    }
    return response;
  }

  // ============================================================================
  // PATTERN 13: PERFORMANCE THRESHOLDS (10 points)
  // ============================================================================
  const PERFORMANCE_THRESHOLDS = Object.freeze({
    ADD_TO_CART: 2000, // ms - network ops threshold
    CART_PREPARATION: 50, // ms - PPD template preparation
    ATTRIBUTES_UPDATE: 500, // ms - cart attributes update
    PRICE_CALCULATION: 100, // ms - price calculation
    BACKGROUND_PREP: 100, // ms - background preparation
  });

  // ============================================================================
  // PATTERN 16: INITIALIZATION GUARD (10 points)
  // ============================================================================
  let isInitialized = false;

  // ============================================================================
  // PATTERN 6: BOOTSTRAP RUNTIME PROTECTION (10 points)
  // ============================================================================
  let bootstrapComplete = false;
  let pendingCalls = [];

  // ============================================================================
  // CASCADE GATE: Prevent cart submission while a cascade auto-selection is in
  // flight. Cascade steps mutate the state manager synchronously, but the final
  // BUSINESS_CASCADE_AUTO_SELECTION_COMPLETED event fires after a 50ms debounce.
  // Submitting the cart within that window would serialize stale selections into
  // _Config_Summary and _Configuration_URL. See: mixed-finish-state post-mortem.
  // ============================================================================
  let cascadeActive = false;

  // ============================================================================
  // PATTERN 15: CLEANUP ARRAYS (10 points)
  // ============================================================================
  let cleanupHandlers = [];

  // ============================================================================
  // PATTERN 2: publishEvent() ASYNC HELPER (10 points)
  // PATTERN 11: ASYNC EVENT PUBLISHING (10 points)
  // NOTE: Object.freeze() NOT needed - CoreEventBus.publish() freezes centrally (Pattern 18 DRY)
  // ============================================================================
  async function publishEvent(eventType, eventData) {
    try {
      if (CoreEventBus?.publish) {
        await CoreEventBus.publish(eventType, eventData);
      } else {
        isDev() &&
          console.warn(
            `[CartManager] Event bus unavailable, failed to publish: ${eventType}`
          );
      }
    } catch (error) {
      isDev() &&
        console.error(`[CartManager] Error publishing ${eventType}:`, error);
    }
  }

  // ============================================================================
  // PATTERN 12: PERFORMANCE TRACKING (10 points)
  // ============================================================================
  function trackPerformance(
    operationType,
    duration,
    eventType,
    extraPayload = {}
  ) {
    const threshold = PERFORMANCE_THRESHOLDS[operationType];
    if (threshold && duration > threshold) {
      publishEvent(eventType, {
        operation: operationType,
        executionTime: duration.toFixed(2),
        threshold,
        timestamp: Date.now(),
        source: 'cart-manager',
        ...extraPayload,
      });

      isDev() &&
        console.warn(
          `[CartManager] PERFORMANCE: ${operationType} took ${duration.toFixed(2)}ms (threshold: ${threshold}ms)`
        );
    }
  }

  // ============================================================================
  // PATTERN 4: EVENT CONSUMER HANDLERS (10 points)
  // PATTERN 5: EVENT REPLAY READY (10 points) - Bootstrap queue replays pending calls
  // PATTERN 6: BOOTSTRAP SUBSCRIPTION AT MODULE LOAD TIME (10 points)
  // ============================================================================
  if (CoreEventBus) {
    const unsubBootstrap = CoreEventBus.subscribe(
      EVENTS.SYSTEM_STATE_FROM_PPD_READY,
      () => {
        isDev() &&
          console.log('[CartManager] Bootstrap complete event received');
        bootstrapComplete = true;

        // PATTERN 5: Replay queued calls from before bootstrap
        if (pendingCalls.length > 0) {
          isDev() &&
            console.log(
              `[CartManager] Processing ${pendingCalls.length} queued calls`
            );
          pendingCalls.forEach((call) => {
            try {
              call.fn.apply(call.context, call.args);
            } catch (error) {
              isDev() &&
                console.error(
                  '[CartManager] Error processing queued call:',
                  error
                );
            }
          });
          pendingCalls = [];
        }
      }
    );
    cleanupHandlers.push(unsubBootstrap);

    isDev() &&
      console.log(
        '[CartManager] Subscribed to bootstrap event at module load time'
      );
  }

  /**
   * Publishes add_to_cart_failed WITH A REASON to both the GTM/GA4 dataLayer and
   * the Super App Web Pixel. Mirror of assets/cart-drawer.js's _showApoAddError().
   *
   * Why this exists: all five of this module's failure paths published
   * EVENTS.BUSINESS_CART_ADD_FAILED on the INTERNAL CCP event bus only, so a
   * configurator add that failed reached no analytics lane at all — while the
   * success path directly beside it (STAGE 4) has dual-published since it
   * shipped. Every add_to_cart_failed row in store_visits came from the APO or
   * native paths; the Design Studio surface was entirely dark.
   *
   * 🚨 The reason rides in element_label ON PURPOSE. apps.super-app.track.tsx
   * persists exactly four element_* fields (elementAction / elementSection /
   * elementLabel / elementPosition); a bespoke `failure_reason` key would be
   * accepted by the beacon and then silently dropped at the INSERT, looking
   * correct forever while reaching nothing — the same shape of bug as
   * willitfit.js's `will_it_fit_complete` event. Documented identically in
   * assets/cart-drawer.js and assets/product-form.js.
   *
   * Slugs name the failure PATH and are deliberately low-cardinality so they
   * GROUP BY cleanly. The underlying error strings are NOT shipped: they
   * interpolate variant ids, titles and counts, which would make the field
   * ungroupable for the exact analysis it exists to serve.
   *
   * @param {string} reason Low-cardinality slug naming the failure path.
   */
  function publishAtcFailedAnalytics(reason) {
    // Staff traffic is never published (snippets/md-internal-traffic.liquid).
    if (window.MD_INTERNAL_TRAFFIC === true) return;
    try {
      const payload = {
        element_action: 'add_to_cart_failed',
        // Matches this module's success emitter so the two group together, and
        // stays distinct from the APO/native emitters' 'pdp_main'.
        element_section: 'configurator',
        element_label: reason || 'unknown',
        element_position: 'primary_cta',
        page_type: document.body.dataset.pageType || '',
        product_id: document.body.dataset.productId || '',
      };
      // Assign-or-create rather than the success emitter's `if (window.dataLayer)`:
      // a failure fired before GTM boots should be queued, not dropped. Losing a
      // success event costs a duplicate-ish datapoint; losing a failure event
      // recreates the exact blind spot this function was added to close.
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({ event: 'element_click', ...payload });
      if (window.Shopify?.analytics?.publish)
        window.Shopify.analytics.publish('md_element_click', payload);
    } catch (_e) {
      /* analytics failure must never block the cart flow */
    }
  }

  /**
   * Publishes the OUTCOME of a partial-add rollback (_rollbackAttemptLines), so
   * a failed rollback (a short door may still be in the cart) is countable
   * instead of visible only in the shopper's modal.
   *
   * element_label is `<status>|removed_<N>`: status is 'none' | 'rolled_back' |
   * 'failed', and both parts are low-cardinality so store_visits can GROUP BY
   * it (the same `slug|count` shape as `build_this_partial`). The pixel
   * persists only the four element_* fields, so configuration_id,
   * removed_count and cart_needs_review ride on the dataLayer push only (GA4 /
   * GTM). The configuration id is a random per-attempt token, not PII.
   *
   * @param {{status: string, removedCount: number}} rollback
   * @param {string} configId - the attempt's _Configuration_ID
   */
  function publishRollbackAnalytics(rollback, configId) {
    if (window.MD_INTERNAL_TRAFFIC === true) return;
    try {
      const status = (rollback && rollback.status) || 'unknown';
      const removed = Number(rollback && rollback.removedCount) || 0;
      const payload = {
        element_action: 'add_to_cart_rollback',
        element_section: 'configurator',
        element_label: `${status}|removed_${removed}`,
        element_position: 'primary_cta',
        page_type: document.body.dataset.pageType || '',
        product_id: document.body.dataset.productId || '',
      };
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({
        event: 'element_click',
        ...payload,
        configuration_id: configId || '',
        removed_count: removed,
        cart_needs_review: status === 'failed',
      });
      if (window.Shopify?.analytics?.publish)
        window.Shopify.analytics.publish('md_element_click', payload);
    } catch (_e) {
      /* analytics failure must never block the cart flow */
    }
  }

  /**
   * Publishes whether an add carried the stored configured-door image
   * (_configuredDoorImageForAdd), so the fallback rate (merged line shows the
   * parent product's photo until the cart backfill runs) is countable.
   * element_label is a low-cardinality `included` | `omitted|<reason>`; the pixel persists only
   * the four element_* fields, so the configuration id rides on the dataLayer
   * push only. Staff traffic is never published, like the emitters above.
   *
   * @param {string} outcome
   * @param {string} configId - the attempt's _Configuration_ID
   */
  function publishConfiguredDoorImageAnalytics(outcome, configId) {
    if (window.MD_INTERNAL_TRAFFIC === true) return;
    try {
      const payload = {
        element_action: 'configured_door_image',
        element_section: 'configurator',
        element_label: outcome || 'unknown',
        element_position: 'primary_cta',
        page_type: document.body.dataset.pageType || '',
        product_id: document.body.dataset.productId || '',
      };
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({
        event: 'element_click',
        ...payload,
        configuration_id: configId || '',
      });
      if (window.Shopify?.analytics?.publish)
        window.Shopify.analytics.publish('md_element_click', payload);
    } catch (_e) {
      /* analytics failure must never block the cart flow */
    }
  }

  /**
   * The base line's _Configuration_URL for a selections snapshot: the
   * conductor's serialized configuration state, else the page's own query
   * string minus a build-preset handle. ONE builder for the add
   * (prepareCartItems) and the image prefetch, so the prefetch cache key and
   * the add's key come from exactly the same string.
   *
   * @param {Object|null} conductor
   * @param {Object|null} selections - this._selections
   * @returns {string}
   */
  function buildConfigurationUrl(conductor, selections) {
    let currentUrl = stripBuildPresetParam(window.location.search);
    if (conductor && typeof conductor.serializeConfigurationState === 'function') {
      const serialized = conductor.serializeConfigurationState(
        (selections && selections.selections) || selections
      );
      if (serialized) currentUrl = `?config=${serialized}`;
    }
    return currentUrl;
  }

  /**
   * Short stable key for a configuration state (32-bit FNV-1a, base 36), so the
   * image prefetch cache never holds the full serialized state. Also the tail
   * of the upload's `config_<ms>_<key>` id, which the route's pattern accepts.
   *
   * @param {string} configurationUrl - a _Configuration_URL
   * @returns {string} '' for an empty state
   */
  function configurationStateKey(configurationUrl) {
    if (typeof configurationUrl !== 'string' || configurationUrl === '') return '';
    let hash = 0x811c9dc5;
    for (let i = 0; i < configurationUrl.length; i++) {
      hash ^= configurationUrl.charCodeAt(i);
      hash = Math.imul(hash, 0x01000193) >>> 0;
    }
    return hash.toString(36);
  }

  /** The configurator BASE line of a payload or cart: it alone carries _Configuration_URL. */
  function findConfiguredBaseItem(items) {
    return (
      (items || []).find(
        (item) =>
          item &&
          item.properties &&
          item.properties._Configuration_URL !== undefined &&
          !item.properties._Add_on_for
      ) || null
    );
  }

  /**
   * Doors of this attempt that landed MERGED: the summed quantity of the
   * has_components lines (Super App Cart Transform, Gap Plan #25b) whose
   * _Configuration_ID is this attempt's. 0 when the door landed flat, which is
   * every cart while the transform is gated or deleted. Mirrors cart.js
   * mdIsMergedConfigLine (has_components === true).
   * @param {Array<object>|undefined} lines - /cart/add.js or /cart.js items
   * @param {string} configId - this attempt's _Configuration_ID
   * @returns {number}
   */
  function mergedDoorQuantityFor(lines, configId) {
    if (!configId) return 0;
    return (lines || []).reduce(
      (sum, line) =>
        line &&
        line.has_components === true &&
        line.properties &&
        line.properties._Configuration_ID === configId
          ? sum + (Number(line.quantity) || 0)
          : sum,
      0
    );
  }

  // ============================================================================
  // MODULE DEFINITION
  // PATTERN 20: CONDUCTOR SINGLE PUBLICATION (10 points)
  // ============================================================================
  window.MurphyDoorConfigurator.cartManager = {
    // Internal state for event-driven data access
    _productData: null,
    _cartMutations: null,
    _selections: null,
    /**
     * Pre-built cart items cache (Instagram Optimization)
     *
     * Cache Strategy: Invalidate cart items cache whenever source data changes
     * - Product data changes -> cart templates may be stale
     * - Selections change -> cart items need rebuilding
     * - Configuration complete -> background prep rebuilds cache
     * - A cart add CONSUMES it (see _performCartAdd): every add gets a fresh
     *   _Configuration_ID, so a second tap can never re-POST the same lines
     */
    _cachedCartItems: null,
    // Timestamp of the add in flight (0 = none). A tap during an add is
    // ignored; stale after ADD_IN_FLIGHT_STALE_MS so a callback that never
    // fires cannot disable Add to Cart for the rest of the visit.
    _addInFlightSince: 0,
    _pendingProductDataCallbacks: [],
    _pendingSelectionsCallbacks: [],

    // ============================================================================
    // PATTERN 4: EVENT CONSUMER HANDLER - Initialize subscriptions
    // ============================================================================
    initialize() {
      // PATTERN 16: Initialization guard
      if (isInitialized) {
        isDev() && console.warn('[CartManager] Already initialized, skipping');
        return;
      }

      if (!CoreEventBus) {
        isDev() &&
          console.error(
            '[CartManager] CoreEventBus not available, cannot initialize'
          );
        return;
      }

      // Subscribe to system events for coordination
      cleanupHandlers.push(
        CoreEventBus.subscribe(
          EVENTS.SYSTEM_READY,
          this.handleSystemReady.bind(this)
        )
      );
      cleanupHandlers.push(
        CoreEventBus.subscribe(
          EVENTS.SYSTEM_STATE_CHANGE,
          this.handleStateChange.bind(this)
        )
      );
      cleanupHandlers.push(
        CoreEventBus.subscribe(
          EVENTS.DATA_PRODUCT_DATA_RESPONSE,
          this.handleProductDataResponse.bind(this)
        )
      );
      cleanupHandlers.push(
        CoreEventBus.subscribe(
          EVENTS.DATA_CART_MUTATIONS_RESPONSE,
          this.handleCartMutationsResponse.bind(this)
        )
      );
      cleanupHandlers.push(
        CoreEventBus.subscribe(
          EVENTS.STATE_SELECTIONS_RESPONSE,
          this.handleSelectionsResponse.bind(this)
        )
      );

      // Pre-build cart items when configuration is complete
      cleanupHandlers.push(
        CoreEventBus.subscribe(
          EVENTS.VALIDATION_CONFIGURATION_COMPLETE,
          this.handleConfigurationComplete.bind(this)
        )
      );

      // Prefetch cart data when Summary tab activates
      cleanupHandlers.push(
        CoreEventBus.subscribe(
          EVENTS.UI_SUMMARY_TAB_ACTIVATED,
          this.handleSummaryTabActivated.bind(this)
        )
      );

      // Stored configured-door image: ui-updater asks for a pre-click prefetch
      // (Add to Cart pointer/focus, or configuration idle with it on screen).
      if (CoreEventBus) {
        cleanupHandlers.push(
          CoreEventBus.subscribe(
            EVENTS.UI_CONFIGURED_DOOR_IMAGE_PREFETCH_REQUESTED,
            () => {
              this._prefetchConfiguredDoorImage();
            }
          )
        );
      }

      // Invalidate cache when selections are unapplied
      cleanupHandlers.push(
        CoreEventBus.subscribe(
          EVENTS.SELECTION_USER_UNAPPLY_COMPLETED,
          this.handleUnapplyCompleted.bind(this)
        )
      );
      // Also invalidate on failure -- other state may have partially changed
      cleanupHandlers.push(
        CoreEventBus.subscribe(
          EVENTS.SELECTION_USER_UNAPPLY_FAILED,
          this.handleUnapplyCompleted.bind(this)
        )
      );
      // ...and on every other clear path (cascade resets, a slider left on a
      // forbidden stop, the ADD_TO_CART readiness gate). None of those fire
      // SELECTION_USER_UNAPPLY_COMPLETED, so pre-built cart items could still
      // carry a line for a category that is no longer applied.
      cleanupHandlers.push(
        CoreEventBus.subscribe(
          CONSTANTS.EVENTS.STATE_SELECTION_CLEARED,
          this.handleUnapplyCompleted.bind(this)
        )
      );

      // Invalidate cache when selections are applied or changed.
      // Without this, _cachedCartItems and _selections remain stale after a user
      // changes an option (e.g. Width 30→32) because prepareCartItemsInBackground
      // built the cache at CONFIGURATION_COMPLETE and no event cleared it.
      cleanupHandlers.push(
        CoreEventBus.subscribe(
          EVENTS.SELECTION_VARIANT_APPLIED,
          this.handleSelectionApplied.bind(this)
        )
      );
      cleanupHandlers.push(
        CoreEventBus.subscribe(
          EVENTS.SELECTION_AUTO_APPLY_COMPLETED,
          this.handleSelectionApplied.bind(this)
        )
      );

      // CASCADE GATE: Track cascade lifecycle to prevent cart submission with
      // mid-cascade (possibly inconsistent) state. On COMPLETED or FAILED, the
      // cached selections snapshot is invalidated so the next addToCart fetches
      // settled state.
      cleanupHandlers.push(
        CoreEventBus.subscribe(
          CONSTANTS.EVENTS.BUSINESS_CASCADE_AUTO_SELECTION_STARTED,
          this.handleCascadeStarted.bind(this)
        )
      );
      cleanupHandlers.push(
        CoreEventBus.subscribe(
          CONSTANTS.EVENTS.BUSINESS_CASCADE_AUTO_SELECTION_COMPLETED,
          () => this.handleCascadeCompleted({ status: 'completed' })
        )
      );
      cleanupHandlers.push(
        CoreEventBus.subscribe(
          CONSTANTS.EVENTS.BUSINESS_CASCADE_AUTO_SELECTION_FAILED,
          () => this.handleCascadeCompleted({ status: 'failed' })
        )
      );

      isInitialized = true;

      publishEvent(EVENTS.MODULE_CART_MANAGER_READY, {
        module: 'cartManager',
        timestamp: Date.now(),
      });

      isDev() &&
        console.log('[CartManager] Initialized with event subscriptions');
    },

    // ============================================================================
    // EVENT CONSUMER HANDLERS
    // ============================================================================
    handleSystemReady() {
      isDev() && console.log('[CartManager] System ready, cart manager active');
    },

    handleStateChange(data) {
      isDev() && console.log('[CartManager] State change received', data);
    },

    handleProductDataResponse(data) {
      this._productData = data.productData;

      // Invalidate cache with event publication
      const hadCache = !!this._cachedCartItems;
      this._cachedCartItems = null;

      if (hadCache) {
        publishEvent(EVENTS.BUSINESS_CART_CACHE_INVALIDATED, {
          reason: 'product_data_changed',
          timestamp: Date.now(),
          source: 'cart-manager.handleProductDataResponse',
        });
        isDev() &&
          console.log('[CartManager] Cache invalidated - product data changed');
      }

      isDev() &&
        console.log(
          '[CartManager] Product data received via conductor delegation'
        );

      // Fire all pending callbacks and clear the queue
      const callbacks = this._pendingProductDataCallbacks;
      this._pendingProductDataCallbacks = [];
      isDev() &&
        console.log(
          `[CartManager] Executing ${callbacks.length} queued callbacks`
        );
      callbacks.forEach((cb) => cb());
    },

    handleCartMutationsResponse(data) {
      this._cartMutations = data.cartMutations;
      isDev() &&
        console.log(
          '[CartManager] Cart mutations received via conductor delegation'
        );
    },

    handleSelectionsResponse(data) {
      this._selections = data.selections;
      isDev() &&
        console.log(
          '[CartManager] Selections received via conductor delegation'
        );

      // Invalidate cache with event publication
      const hadCache = !!this._cachedCartItems;
      this._cachedCartItems = null;

      if (hadCache) {
        publishEvent(EVENTS.BUSINESS_CART_CACHE_INVALIDATED, {
          reason: 'selections_changed',
          timestamp: Date.now(),
          source: 'cart-manager.handleSelectionsResponse',
        });
        isDev() &&
          console.log('[CartManager] Cache invalidated - selections changed');
      }

      // Fire all pending callbacks and clear the queue
      const callbacks = this._pendingSelectionsCallbacks;
      this._pendingSelectionsCallbacks = [];
      isDev() &&
        console.log(
          `[CartManager] Executing ${callbacks.length} queued callbacks`
        );
      callbacks.forEach((cb) => cb());
    },

    // Pre-build cart items when configuration completes
    handleConfigurationComplete(data) {
      isDev() &&
        console.log(
          '[CartManager] Configuration complete, pre-building cart items in background'
        );
      this._prepareCartItemsInBackground();
    },

    // Invalidate cache when a selection is unapplied
    handleUnapplyCompleted(data) {
      const hadCache = !!this._cachedCartItems;
      this._cachedCartItems = null;
      this._selections = null; // force fresh fetch on next add

      if (hadCache) {
        publishEvent(EVENTS.BUSINESS_CART_CACHE_INVALIDATED, {
          reason: 'selection_unapplied',
          categoryId: data?.categoryId,
          timestamp: Date.now(),
          source: 'cart-manager.handleUnapplyCompleted',
        });
      }

      isDev() &&
        console.log(
          '[CartManager] Cache invalidated - selection unapplied',
          data?.categoryId
        );
    },

    // Invalidate cache when a selection is applied or auto-applied.
    // Mirrors handleUnapplyCompleted — ensures _cachedCartItems and _selections
    // reflect the latest conductor state after any selection change.
    handleSelectionApplied(data) {
      const hadCache = !!this._cachedCartItems;
      this._cachedCartItems = null;
      this._selections = null; // force fresh fetch on next add

      if (hadCache) {
        publishEvent(EVENTS.BUSINESS_CART_CACHE_INVALIDATED, {
          reason: 'selection_applied',
          categoryId: data?.categoryId,
          variantId: data?.variantId,
          timestamp: Date.now(),
          source: 'cart-manager.handleSelectionApplied',
        });
      }

      isDev() &&
        console.log(
          '[CartManager] Cache invalidated - selection applied',
          data?.categoryId
        );
    },

    // CASCADE GATE handlers — set/clear cascadeActive flag and invalidate stale cache.
    // The UI layer (md-config-ui-updater.js) disables the Add to Cart button while
    // cascadeActive=true; this guard is defense-in-depth for the race window between
    // cascade queue drain and BUSINESS_CASCADE_AUTO_SELECTION_COMPLETED (50ms debounce).
    handleCascadeStarted() {
      cascadeActive = true;
      isDev() &&
        console.log('[CartManager] Cascade started — cart submission gated');
    },

    handleCascadeCompleted({ status = 'completed' } = {}) {
      cascadeActive = false;
      // Invalidate selections/cache snapshot captured before cascade settled.
      // Forces a fresh fetch of conductor state on the next addToCart call.
      this._selections = null;
      this._cachedCartItems = null;
      isDev() &&
        console.log(
          `[CartManager] Cascade ${status} — cart submission gate released, cache invalidated`
        );
    },

    // Prefetch cart data when Summary tab activates
    handleSummaryTabActivated(data) {
      isDev() &&
        console.log(
          '[CartManager] Summary tab activated, pre-warming cart data cache'
        );
      this.prefetchCartData();
    },

    // Eagerly fetch and cache cart-related data
    prefetchCartData() {
      isDev() && console.log('[CartManager] Prefetching cart data structures');

      // Prefetch product data if not already cached
      if (!this._productData) {
        this.requestProductData(() => {
          isDev() && console.log('[CartManager] Product data prefetched');
        });
      }

      // Prefetch selections if not already cached
      if (!this._selections) {
        this.requestSelections(() => {
          isDev() && console.log('[CartManager] Selections prefetched');
        });
      }

      // Prefetch cart mutations if not already cached
      if (!this._cartMutations) {
        this.requestCartMutations();
      }

      isDev() && console.log('[CartManager] Cart data prefetch initiated');
    },

    // ============================================================================
    // PATTERN 3: 5-STAGE EVENT LIFECYCLE - Background Preparation
    // PATTERN 14: TRY/CATCH + FAILED EVENTS (10 points)
    // ============================================================================
    async _prepareCartItemsInBackground() {
      const startTime = window.performance
        ? window.performance.now()
        : Date.now();

      try {
        // STAGE 1: START
        publishEvent(EVENTS.BUSINESS_CART_BACKGROUND_PREP_STARTED, {
          source: 'cart-manager._prepareCartItemsInBackground',
          timestamp: Date.now(),
        });

        isDev() &&
          console.log('[CartManager] Starting background cart preparation');

        // STAGE 2/3: PROGRESS - Ensure we have all required data
        if (!this._productData || !this._selections) {
          isDev() &&
            console.log(
              '[CartManager] Prefetching required data for cart items'
            );

          try {
            if (!this._productData) {
              await new Promise((resolve, reject) => {
                const timeoutId = setTimeout(() => {
                  reject(
                    new Error(
                      `requestProductData timed out after ${BACKGROUND_ASYNC_TIMEOUT_MS / 1000} seconds`
                    )
                  );
                }, BACKGROUND_ASYNC_TIMEOUT_MS);
                this.requestProductData(() => {
                  clearTimeout(timeoutId);
                  resolve();
                });
              });
            }

            if (!this._selections) {
              await new Promise((resolve, reject) => {
                const timeoutId = setTimeout(() => {
                  reject(
                    new Error(
                      `requestSelections timed out after ${BACKGROUND_ASYNC_TIMEOUT_MS / 1000} seconds`
                    )
                  );
                }, BACKGROUND_ASYNC_TIMEOUT_MS);
                this.requestSelections(() => {
                  clearTimeout(timeoutId);
                  resolve();
                });
              });
            }

            // Only build cache after both data sources are confirmed available
            if (this._productData && this._selections) {
              this._buildAndCacheCartItems();
            }
          } catch (error) {
            isDev() &&
              console.error('[CartManager] Failed to prefetch data:', error);
          }
        } else {
          await this._buildAndCacheCartItems();
        }

        // STAGE 4: SUCCESS + performance tracking
        const endTime = window.performance
          ? window.performance.now()
          : Date.now();
        const executionTime = endTime - startTime;

        // PATTERN 12: Performance tracking
        trackPerformance(
          'BACKGROUND_PREP',
          executionTime,
          EVENTS.PERFORMANCE_CART_BACKGROUND_PREP_SLOW
        );

        publishEvent(EVENTS.BUSINESS_CART_BACKGROUND_PREP_COMPLETED, {
          executionTime: executionTime.toFixed(2),
          cacheBuilt: !!this._cachedCartItems,
          timestamp: Date.now(),
          source: 'cart-manager._prepareCartItemsInBackground',
        });

        isDev() &&
          console.log(
            `[CartManager] Background prep completed in ${executionTime.toFixed(2)}ms`
          );
      } catch (error) {
        // STAGE 5: FAILED
        isDev() &&
          console.error('[CartManager] Background preparation failed:', error);
        publishEvent(EVENTS.BUSINESS_CART_BACKGROUND_PREP_FAILED, {
          error: error.message,
          source: 'cart-manager._prepareCartItemsInBackground',
          timestamp: Date.now(),
        });
      }
    },

    // Build and cache cart items
    async _buildAndCacheCartItems() {
      try {
        const items = await this.prepareCartItems();
        if (items && items.length > 0) {
          this._cachedCartItems = items;

          publishEvent(EVENTS.BUSINESS_CART_CACHE_WARMED, {
            itemCount: items.length,
            cacheTimestamp: Date.now(),
            source: 'cart-manager._buildAndCacheCartItems',
          });

          isDev() &&
            console.log('[CartManager] Cart items pre-built and cached!', {
              itemCount: items.length,
            });
        }
      } catch (error) {
        isDev() &&
          console.error('[CartManager] Failed to pre-build cart items:', error);
        this._cachedCartItems = null;
        throw error;
      }
    },

    // ============================================================================
    // PATTERN 9: DATA REQUEST EVENTS (10 points)
    // ============================================================================
    requestProductData(callback) {
      isDev() &&
        console.log('[CartManager] Requesting product data via event system');

      // If data already cached, execute callback immediately
      if (this._productData) {
        isDev() && console.log('[CartManager] Using cached product data');
        callback();
        return;
      }

      // Track whether response was received for timeout fallback
      let responseReceived = false;
      const wrappedCallback = () => {
        responseReceived = true;
        callback();
      };

      // Queue the wrapped callback
      this._pendingProductDataCallbacks.push(wrappedCallback);

      // Only publish the request if this is the first listener
      if (this._pendingProductDataCallbacks.length === 1) {
        isDev() &&
          console.log('[CartManager] Publishing product data request event');
        publishEvent(EVENTS.DATA_REQUEST_PRODUCT_STRUCTURE, {
          requester: 'cart-manager',
          callback: 'product_data_response',
          timestamp: Date.now(),
        });
      } else {
        isDev() &&
          console.log(
            '[CartManager] Product data request already in flight, queuing callback'
          );
      }

      // Timeout: drain pending callbacks so the outer addToCart try/catch
      // receives control and fires BUSINESS_CART_ADD_FAILED cleanly.
      // No direct conductor access — event-driven architecture only (CCP Pattern #8).
      setTimeout(() => {
        if (!responseReceived) {
          isDev() &&
            console.error(
              '[CartManager] Product data event response timeout — conductor did not respond to DATA_REQUEST_PRODUCT_STRUCTURE'
            );
          const pendingCallbacks = this._pendingProductDataCallbacks;
          this._pendingProductDataCallbacks = [];
          // _productData remains null; callbacks will fail at getProductData(),
          // which propagates to the outer try/catch in addToCart.
          pendingCallbacks.forEach((cb) => {
            try {
              cb();
            } catch (_) {}
          });
        }
      }, ASYNC_REQUEST_TIMEOUT_MS);
    },

    requestCartMutations() {
      publishEvent(EVENTS.DATA_REQUEST_CART_MUTATIONS, {
        requester: 'cart-manager',
        timestamp: Date.now(),
      });
    },

    requestSelections(callback) {
      isDev() &&
        console.log('[CartManager] Requesting selections via event system');

      // If selections already cached, execute callback immediately
      if (this._selections) {
        isDev() && console.log('[CartManager] Using cached selections');
        callback();
        return;
      }

      // Track whether response was received for timeout fallback
      let responseReceived = false;
      const wrappedCallback = () => {
        responseReceived = true;
        callback();
      };

      // Queue the wrapped callback
      this._pendingSelectionsCallbacks.push(wrappedCallback);

      // Only publish the request if this is the first listener
      if (this._pendingSelectionsCallbacks.length === 1) {
        isDev() &&
          console.log('[CartManager] Publishing selections request event');
        publishEvent(EVENTS.STATE_REQUEST_CART_CONFIGURATION, {
          requester: 'cart-manager',
          callback: 'selections_response',
          timestamp: Date.now(),
        });
      } else {
        isDev() &&
          console.log(
            '[CartManager] Selections request already in flight, queuing callback'
          );
      }

      // Timeout: drain pending callbacks so the outer addToCart try/catch
      // receives control and fires BUSINESS_CART_ADD_FAILED cleanly.
      // No direct conductor access — event-driven architecture only (CCP Pattern #8).
      setTimeout(() => {
        if (!responseReceived) {
          isDev() &&
            console.error(
              '[CartManager] Selections event response timeout — conductor did not respond to STATE_REQUEST_CART_CONFIGURATION'
            );
          const pendingCallbacks = this._pendingSelectionsCallbacks;
          this._pendingSelectionsCallbacks = [];
          // _selections remains null; callbacks will fail at _performCartAdd,
          // which propagates to the outer try/catch in addToCart.
          pendingCallbacks.forEach((cb) => {
            try {
              cb();
            } catch (_) {}
          });
        }
      }, ASYNC_REQUEST_TIMEOUT_MS);
    },

    /**
     * Generic async data request helper (DRY pattern)
     * @param {string} requestEvent - Event to publish
     * @param {string} responseEvent - Event to subscribe to
     * @param {Object} payload - Request payload
     * @param {string} timeoutMessage - Error message for timeout
     * @param {number} timeoutMs - Timeout in milliseconds
     * @returns {Promise<Object|null>} Response data
     */
    async _requestAsyncData(
      requestEvent,
      responseEvent,
      payload,
      timeoutMessage,
      timeoutMs = ASYNC_REQUEST_TIMEOUT_MS
    ) {
      return new Promise((resolve, reject) => {
        const requestId = `${requestEvent}_${Date.now()}_${Math.random().toString(36).substring(2, 11)}`;
        const timeout = setTimeout(() => {
          CoreEventBus.unsubscribe(responseEvent, handleResponse);
          reject(new Error(timeoutMessage));
        }, timeoutMs);

        const handleResponse = (data) => {
          if (data.requestId === requestId) {
            clearTimeout(timeout);
            CoreEventBus.unsubscribe(responseEvent, handleResponse);
            resolve(data);
          }
        };

        CoreEventBus.subscribe(responseEvent, handleResponse);
        CoreEventBus.publish(requestEvent, {
          ...payload,
          requestId,
          requester: 'cart-manager',
          timestamp: Date.now(),
        });
      });
    },

    /**
     * Helper method to throw architectural violation errors with stack traces
     * Centralizes error handling for PPD-First enforcement
     * @param {string} message - The error message to log and throw
     * @throws {Error} Always throws with the provided message
     */
    _throwArchitecturalError(message) {
      isDev() && console.error(message, new Error().stack);
      throw new Error(message);
    },

    /**
     * PPD-FIRST ENFORCEMENT: This method is PROHIBITED
     * Use PPD.cartItemTemplates for O(1) template reads
     * @throws {Error} Always throws - enforces architectural purity
     */
    _requestProductByVariantId(variantId) {
      this._throwArchitecturalError(
        'CRITICAL: _requestProductByVariantId() is PROHIBITED in PPD-First architecture. ' +
          'Cart item preparation MUST use pre-built PPD.cartItemTemplates (O(1) lookups). ' +
          'Use ppd.cartItemTemplates.get(variantId) instead.'
      );
    },

    /**
     * PPD-FIRST ENFORCEMENT: This method is PROHIBITED
     * Use PPD.cartItemTemplates for O(1) template reads
     * @throws {Error} Always throws - enforces architectural purity
     */
    _requestVariantById(variantId) {
      this._throwArchitecturalError(
        'CRITICAL: _requestVariantById() is PROHIBITED in PPD-First architecture. ' +
          'Cart item preparation MUST use pre-built PPD.cartItemTemplates (O(1) lookups). ' +
          'Use ppd.cartItemTemplates.get(variantId) instead.'
      );
    },

    /**
     * PPD-FIRST ENFORCEMENT: This method is PROHIBITED
     * Use PPD.cartItemTemplates for O(1) template reads
     * @throws {Error} Always throws - enforces architectural purity
     */
    _requestProductById(productId) {
      this._throwArchitecturalError(
        'CRITICAL: _requestProductById() is PROHIBITED in PPD-First architecture. ' +
          'Cart item preparation MUST use pre-built PPD.cartItemTemplates which are keyed by variantId. ' +
          'Direct lookups by productId are not supported.'
      );
    },

    // Validates cart item has required properties and valid data
    validateCartItem(item) {
      const parsedId = parseInt(item?.id);
      return (
        item &&
        Number.isInteger(parsedId) &&
        parsedId > 0 &&
        item.quantity > 0 &&
        item.properties &&
        Object.keys(item.properties).length > 0
      );
    },

    // CCP COMPLIANT: Use cached data from conductor delegation
    getCartMutations() {
      return this._cartMutations;
    },

    getProductData() {
      return this._productData;
    },

    // ============================================================================
    // PATTERN 3: 5-STAGE EVENT LIFECYCLE - Add to Cart
    // PATTERN 6: BOOTSTRAP RUNTIME PROTECTION (10 points)
    // PATTERN 10: UI UPDATE EVENTS (10 points)
    // PATTERN 14: TRY/CATCH + FAILED EVENTS (10 points)
    // ============================================================================
    addToCart(configuration) {
      // PATTERN 6: Bootstrap barrier - queue if PPD not ready
      if (!bootstrapComplete) {
        isDev() &&
          console.log('[CartManager] addToCart queued - waiting for bootstrap');
        pendingCalls.push({
          fn: this.addToCart,
          context: this,
          args: [configuration],
        });
        return;
      }

      // CASCADE GATE: Reject cart submission while a cascade auto-selection is
      // in flight. The UI layer disables the Add to Cart button during cascade,
      // but this guard covers the 50ms debounce window between queue drain and
      // BUSINESS_CASCADE_AUTO_SELECTION_COMPLETED. See: mixed-finish post-mortem.
      if (cascadeActive) {
        isDev() &&
          console.warn(
            '[CartManager] addToCart blocked — cascade auto-selection in flight'
          );
        return;
      }

      // READINESS GATE: refuse an unfinished door before any optimistic UI
      // or network work. _performCartAdd re-checks the actual payload.
      const earlyGate = this._checkCartReadiness();
      if (!earlyGate.ok) {
        this._refuseIncompleteCart(earlyGate.readiness, 'cart-manager.addToCart');
        return;
      }

      // DOUBLE-TAP GATE: one add at a time. A second tap while the first is
      // in flight would POST a second door the customer never asked for.
      if (
        this._addInFlightSince &&
        Date.now() - this._addInFlightSince < ADD_IN_FLIGHT_STALE_MS
      ) {
        isDev() &&
          console.warn('[CartManager] addToCart ignored - an add is in flight');
        publishEvent(EVENTS.BUSINESS_CART_ADD_DUPLICATE_IGNORED, {
          source: 'cart-manager.addToCart',
          inFlightMs: Date.now() - this._addInFlightSince,
          timestamp: Date.now(),
        });
        return;
      }
      this._addInFlightSince = Date.now();

      const startTime = window.performance
        ? window.performance.now()
        : Date.now();

      try {
        isDev() &&
          console.log(
            '[CartManager] addToCart called with configuration:',
            configuration
          );

        // STAGE 1: START
        publishEvent(EVENTS.BUSINESS_CART_ADD_STARTED, {
          source: 'cart-manager',
          timestamp: Date.now(),
        });

        // PATTERN 10: UI Update Events - Show optimistic UI immediately
        publishEvent(EVENTS.UI_CART_ADDING_OPTIMISTIC, {
          source: 'cart-manager',
          operation: 'add_to_cart',
          message: 'Adding to cart...',
          timestamp: Date.now(),
        });

        // CCP COMPLIANT: Request UI update via event bus
        publishEvent(EVENTS.UI_SHOW_LOADING_SPINNER, {
          source: 'cart-manager',
          operation: 'add_to_cart',
        });

        isDev() &&
          console.log(
            '[CartManager] Requesting product data for cart operation'
          );

        // PATTERN 9: Use event-driven data request
        this.requestProductData(() => {
          isDev() &&
            console.log('[CartManager] Product data callback executed');

          const productData = this.getProductData();
          isDev() &&
            console.log('[CartManager] Product structure debug:', {
              hasParentProduct: !!productData.baseProduct,
              baseProductType: productData.baseProduct
                ? typeof productData.baseProduct
                : 'undefined',
              baseProductKeys: productData.baseProduct
                ? Object.keys(productData.baseProduct)
                : [],
              hasAddOnProducts: !!productData.addOnProducts,
              addOnProductsSize:
                productData.addOnProducts instanceof Map
                  ? productData.addOnProducts.size
                  : productData.addOnProducts &&
                      typeof productData.addOnProducts === 'object'
                    ? Object.keys(productData.addOnProducts).length
                    : 'unknown',
              baseProductVariantCount:
                productData.baseProduct?.variants?.length || 0,
            });

          this.requestSelections(async () => {
            try {
              isDev() &&
                console.log(
                  '[CartManager] Selections callback executed, calling _performCartAdd'
                );
              await this._performCartAdd(startTime);
            } catch (error) {
              publishAtcFailedAnalytics('selections_error');
              // STAGE 5: FAILED
              publishEvent(EVENTS.BUSINESS_CART_ADD_FAILED, {
                error: `Cart preparation failed: ${error.message}`,
                timestamp: Date.now(),
                source: 'cart-manager.addToCart',
              });
              isDev() &&
                console.error(
                  `[CartManager] addToCart failed during background processing - ${error.message}`
                );
              publishEvent(EVENTS.UI_HIDE_LOADING_SPINNER, {
                source: 'cart-manager',
                operation: 'add_to_cart_error',
              });
            } finally {
              this._addInFlightSince = 0;
            }
          });
        });
      } catch (error) {
        this._addInFlightSince = 0;
        publishAtcFailedAnalytics('init_error');
        // STAGE 5: FAILED
        publishEvent(EVENTS.BUSINESS_CART_ADD_FAILED, {
          error: error.message,
          timestamp: Date.now(),
          source: 'cart-manager.addToCart',
        });

        isDev() &&
          console.error(`[CartManager] addToCart failed - ${error.message}`);

        // Hide loading spinner
        publishEvent(EVENTS.UI_HIDE_LOADING_SPINNER, {
          source: 'cart-manager',
          operation: 'add_to_cart_error',
        });
      }
    },

    /**
     * Is the live APPLIED state cart-ready? Reads conductor.getCartReadiness(),
     * the same answer the Add to Cart button and the conductor's ADD_TO_CART
     * gate use, so the three cannot disagree.
     *
     * @returns {{ok: boolean, readiness: Object|null}}
     */
    _checkCartReadiness() {
      const conductor = window.MurphyDoorConfigurator?.conductor;
      if (!conductor || typeof conductor.getCartReadiness !== 'function') {
        // No way to evaluate; prepareCartItems() cannot run without the
        // conductor either, so this path fails later regardless.
        return { ok: true, readiness: null };
      }
      const readiness = conductor.getCartReadiness();
      return { ok: readiness.ready === true, readiness };
    },

    /**
     * Refuse an add for an unfinished door: no POST, no error modal, and the
     * SAME "missing choice" UX the disabled button uses (auto-navigation
     * scrolls to and flags the first unfinished category). ui-updater answers
     * BUSINESS_CART_ADD_REFUSED_INCOMPLETE by hiding the spinner and undoing
     * the optimistic "Adding to cart..." swap this module may already have
     * requested, so no UI module is called directly from here.
     *
     * @param {Object|null} readiness - conductor.getCartReadiness() result
     * @param {string} source
     */
    _refuseIncompleteCart(readiness, source) {
      const missing = (readiness && readiness.missing) || [];
      isDev() &&
        console.warn(
          '[CartManager] Add to Cart refused - required categories not applied:',
          missing
        );
      publishAtcFailedAnalytics('configuration_incomplete');
      publishEvent(CONSTANTS.EVENTS.BUSINESS_CART_ADD_REFUSED_INCOMPLETE, {
        source,
        missing,
        forbidden: (readiness && readiness.forbidden) || [],
        stale: (readiness && readiness.stale) || [],
        timestamp: Date.now(),
      });
      publishEvent(CONSTANTS.EVENTS.UI_PROGRESS_UPDATE_REQUESTED, {
        source,
        timestamp: Date.now(),
      });
      publishEvent(CONSTANTS.EVENTS.UI_ADD_TO_CART_DISABLED_CLICKED, {
        source,
        missing,
        timestamp: Date.now(),
      });
    },

    /**
     * Last line before /cart/add.js: the payload source (this._selections,
     * which may be cached) must cover every required category with the
     * variant the applied Map holds. One fresh refetch is allowed, since a
     * stale cache is the likely cause; after that, refuse.
     *
     * @returns {Promise<boolean>} true when it is safe to build and POST
     */
    async _ensurePayloadIsComplete() {
      const gate = this._checkCartReadiness();
      if (!gate.ok) {
        this._refuseIncompleteCart(
          gate.readiness,
          'cart-manager._performCartAdd'
        );
        return false;
      }
      if (!gate.readiness || !gate.readiness.known) return true;

      const conductor = window.MurphyDoorConfigurator.conductor;
      const ppd = conductor.getPerfectProductDictionary();
      const required =
        (ppd &&
          ppd.categoryMetadata &&
          ppd.categoryMetadata.allRequiredCategoryIdsArray) ||
        [];
      const findGaps = () => {
        const map = conductor.getAppliedSelectionsMap();
        const raw = this._selections || {};
        const payload = raw.selections || raw;
        return required.filter((categoryId) => {
          const sent = payload[categoryId];
          const applied = map.get(categoryId);
          if (!sent || !applied) return true;
          if (sent.variantId != null || applied.variantId != null) {
            return String(sent.variantId) !== String(applied.variantId);
          }
          return String(sent.productId) !== String(applied.productId);
        });
      };

      let gaps = findGaps();
      if (gaps.length === 0) return true;

      // Stale cache: drop it, ask the conductor again, rebuild items fresh.
      this._selections = null;
      this._cachedCartItems = null;
      await new Promise((resolve) => this.requestSelections(resolve));
      gaps = findGaps();
      if (gaps.length === 0) return true;

      this._refuseIncompleteCart(
        Object.freeze({
          ready: false,
          known: true,
          missing: gaps,
          forbidden: [],
          stale: [],
        }),
        'cart-manager._ensurePayloadIsComplete'
      );
      return false;
    },

    // Internal method to perform cart add after data is available
    async _performCartAdd(startTime) {
      isDev() && console.log('[CartManager] _performCartAdd starting');

      // LAST LINE: a payload missing a required category is never POSTed.
      // The conductor's ADD_TO_CART gate normally stops this earlier; this
      // guards every other route into addToCart and any stale cache here.
      if (!(await this._ensurePayloadIsComplete())) {
        return;
      }

      // Use cached cart items if available
      let items;
      if (this._cachedCartItems && this._cachedCartItems.length > 0) {
        items = this._cachedCartItems;
        // Consume once: the cached lines carry ONE _Configuration_ID. Re-sending
        // them on a later add made Shopify merge every line into quantity 2 of
        // the same door; the next add rebuilds with a fresh ID instead.
        this._cachedCartItems = null;
        isDev() &&
          console.log('[CartManager] Using pre-built cart items from cache!', {
            itemCount: items.length,
          });
      } else {
        items = await this.prepareCartItems();
        isDev() &&
          console.log('[CartManager] Building cart items on-demand:', items);
      }

      isDev() && console.log('[CartManager] Cart items ready:', items);

      // STAGE 2: VALIDATION
      if (items.length === 0 || !items.every(this.validateCartItem)) {
        isDev() &&
          console.log('[CartManager] Cart items validation failed:', {
            itemsLength: items.length,
            validationResults: items.map((item) => ({
              item,
              valid: this.validateCartItem(item),
            })),
          });

        // Hide loading spinner on validation failure
        publishEvent(EVENTS.UI_HIDE_LOADING_SPINNER, {
          source: 'cart-manager',
          operation: 'validation_failed',
        });
        // Report it like every other failed add. Returning silently left the
        // button on "Adding to cart..." with no message and no way to retry.
        publishAtcFailedAnalytics('validation_failed');
        publishEvent(EVENTS.BUSINESS_CART_ADD_FAILED, {
          source: 'cart-manager',
          error: 'Cart items failed validation',
          timestamp: Date.now(),
        });
        return;
      }

      isDev() && console.log('[CartManager] Cart items validated successfully');

      // PATTERN 9: Request config data via conductor delegation
      const conductor = window.MurphyDoorConfigurator?.conductor;
      const sectionId = conductor?.getSectionId?.() || 'unknown';

      // Get configuration details for attributes
      const baseProduct = items.find((item) => item.properties._Base_Product);
      const addOns = items.filter((item) => !item.properties._Base_Product);
      const configId = items[0].properties._Configuration_ID;

      isDev() &&
        console.log('[CartManager] Processing cart items:', {
          totalItems: items.length,
          hasBaseProduct: !!baseProduct,
          addOnsCount: addOns.length,
        });

      if (!baseProduct) {
        isDev() &&
          console.error('[CartManager] No base product found in cart items');

        // Hide loading spinner on error
        publishEvent(EVENTS.UI_HIDE_LOADING_SPINNER, {
          source: 'cart-manager',
          operation: 'base_product_missing',
        });

        publishAtcFailedAnalytics('missing_base_product');
        publishEvent(EVENTS.BUSINESS_CART_ADD_FAILED, {
          source: 'cart-manager',
          error: 'Missing base product',
          timestamp: Date.now(),
        });
        return;
      }

      const configDetails = {
        baseProduct: {
          id: baseProduct.id,
          title: baseProduct.properties._Door_Title,
          price: baseProduct.properties._Price,
        },
        addOns: addOns.map((item) => ({
          id: item.id,
          category: item.properties._Category,
          title: item.properties._Display_Name,
          price: item.properties._Price,
        })),
        timestamp: Date.now(),
        totalPrice: items.reduce(
          (sum, item) => sum + parseFloat(item.properties._Price),
          0
        ),
        origin: window.MurphyDoorConfigurator?.simplifiedModeConfig?.enabled
          ? 'simplified_mode'
          : 'full_configurator',
      };

      isDev() &&
        console.log(
          '[CartManager] Starting fetch request to /cart/add with items:',
          { items }
        );

      // Ask the image export manager to flatten the APPLIED layers into the
      // cart thumbnail for this configuration. Fire-and-forget, in parallel
      // with the add itself: the snapshot must never gate or delay the POST,
      // and the cart surfaces fail open to the stock photo if it never lands.
      publishEvent(EVENTS.UI_CART_IMAGE_SNAPSHOT_REQUESTED, {
        source: 'cart-manager',
        configId,
        timestamp: Date.now(),
      });

      // STAGE 3: PROGRESS - Cart API interaction
      // MW APO BYPASS: Use direct fetch with specific headers to avoid MW APO interference
      // TIMEOUT GUARD: a stalled mobile connection (dropped wifi-to-cellular
      // handoff, captive portal, etc.) can leave fetch() unresolved forever
      // with no error and no success — the spinner just spins. Abort after
      // CART_NETWORK_TIMEOUT_MS so the .catch() below always fires and the
      // customer gets an honest answer instead of an infinite wait.
      // RATE LIMIT: a 429 is retried (bounded, Retry-After aware) by the same
      // helper /cart/update.js uses. This whole configuration is ONE request,
      // so a retry re-sends only that throttled request. Each attempt gets its
      // own timeout; no UI events fire between attempts, so the button stays
      // in its "Adding" state until the final outcome.
      // Stored door picture: include the prefetched URL on the BASE line when
      // this exact configuration's upload is ready, or becomes ready within
      // CONFIGURED_DOOR_IMAGE_ADD_WAIT_MS. Otherwise the add goes out exactly
      // as before. Never a longer wait than that cap.
      const doorImage = await this._configuredDoorImageForAdd(
        findConfiguredBaseItem(items)
      );
      publishConfiguredDoorImageAnalytics(
        doorImage.url ? 'included' : `omitted|${doorImage.reason}`,
        configId
      );
      const payloadItems = doorImage.url
        ? items.map((item) =>
            item === findConfiguredBaseItem(items)
              ? {
                  ...item,
                  properties: {
                    ...item.properties,
                    [CONFIGURATION_IMAGE_PROPERTY]: doorImage.url,
                  },
                }
              : item
          )
        : items;
      const cartAddBody = JSON.stringify({ items: payloadItems });
      // Set once COMPLETED is published: the .catch() below must never roll
      // back a door whose add already succeeded.
      let addSucceeded = false;
      // The add's final HTTP status, once Shopify answered at all. A final 429
      // was refused BEFORE processing (see CART_RATE_LIMIT_RETRY), so nothing
      // landed and there is nothing to sweep.
      let addHttpStatus = 0;
      return fetchWithRateLimitRetry(() => {
        const addToCartController = new AbortController();
        const addToCartTimeoutId = setTimeout(
          () => addToCartController.abort(),
          CART_NETWORK_TIMEOUT_MS
        );
        return fetch(`/cart/add.js`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-Requested-With': 'XMLHttpRequest',
            Accept: 'application/json',
          },
          body: cartAddBody,
          signal: addToCartController.signal,
        }).finally(() => clearTimeout(addToCartTimeoutId));
      }, 'cart/add.js')
        .then((response) => {
          addHttpStatus = response.status;
          isDev() &&
            console.log('[CartManager] Fetch response received:', {
              status: response.status,
              ok: response.ok,
              redirected: response.redirected,
              url: response.url,
            });

          if (!response.ok) {
            return response
              .json()
              .catch(() => null)
              .then((errorBody) => {
                const shopifyDescription =
                  errorBody?.description ||
                  errorBody?.message ||
                  (errorBody ? JSON.stringify(errorBody) : 'No error body');
                isDev() &&
                  console.error(
                    '[CartManager] Shopify cart API rejected items:',
                    {
                      status: response.status,
                      shopifyError: shopifyDescription,
                      rejectedItems: items.map((item) => ({
                        id: item.id,
                        variantId: item.variantId,
                        quantity: item.quantity,
                        category: item.properties?._Category,
                      })),
                    }
                  );
                throw new Error(
                  `HTTP error! status: ${response.status} - ${shopifyDescription}`
                );
              });
          }

          return response.json();
        })
        .then(async (data) => {
          isDev() &&
            console.log(
              '[CartManager] Cart add successful, response data:',
              data
            );

          if (data) {
            // STAGE 3.5: VERIFY all expected items were accepted by Shopify
            // Shopify can silently drop items when inventoryPolicy: DENY and
            // sellableOnlineQuantity <= 0, even when inventoryItem.tracked: false.
            // cart/add.js returns HTTP 200 either way, so the response is checked.
            // This runs BEFORE the attribute write: a failed attempt must not
            // leave a __config_ attribute behind for a door that is not in the cart.
            const expectedCount = items.length;
            const actualCount = data.items?.length || 0;
            // Lines are not enough: a line can land with FEWER units than
            // posted (an app or script rewriting the add), and the line count
            // still matches. A dropped line (add.js drops a DENY line at
            // sellable <= 0) is a zero-unit landing, so it is caught the same way.
            // Units are compared per variant, the same unit contract the
            // Super App Save Cart guard uses. The add response lists only this
            // request's lines, so a landed quantity above the posted one (a
            // merge) is never a shortfall.
            const unitsByVariant = (lines, idOf) =>
              lines.reduce((acc, line) => {
                const id = idOf(line);
                acc.set(id, (acc.get(id) || 0) + (Number(line.quantity) || 0));
                return acc;
              }, new Map());
            const postedUnits = unitsByVariant(items, (item) => item.id);
            const landedUnits = unitsByVariant(
              data.items || [],
              (line) => line.variant_id
            );
            // A door the Super App Cart Transform MERGED at add time (Gap Plan
            // #25b) lands as ONE "Configured Door" parent line carrying this
            // attempt's _Configuration_ID, so the per-variant comparison sees
            // 1 line where 22 were posted. Measured on prod 2026-10-23: the
            // add.js response lists that parent WITHOUT `has_components` (only
            // /cart.js carries the flag) and with no component array. So when
            // the response is short AND holds a line of ours whose variant was
            // never posted, /cart.js is read to ask whether it is a merged door.
            // The merge itself is the completeness proof: the function
            // (super-app extensions/configured-door-merge/src/merge.js) merges
            // a group ONLY when it has exactly one base line, every add-on the
            // base's _Config_Summary lists, and every line at quantity 1. A
            // dropped or short line fails that check and stays FLAT, where the
            // per-variant comparison catches it as before (and a flat drop has
            // no unposted line, so it costs no extra read). This attempt's
            // posted units are then credited per merged door.
            const shortfallOf = (landed) => {
              let missing = 0;
              const shortIds = new Set();
              postedUnits.forEach((posted, variantId) => {
                const short = posted - (landed.get(variantId) || 0);
                if (short > 0) {
                  missing += short;
                  shortIds.add(variantId);
                }
              });
              return { missing, shortIds };
            };
            let mergedDoorQuantity = mergedDoorQuantityFor(data.items, configId);
            const hasUnpostedOwnLine = (data.items || []).some(
              (line) =>
                line &&
                line.properties &&
                line.properties._Configuration_ID === configId &&
                !postedUnits.has(line.variant_id)
            );
            if (
              mergedDoorQuantity === 0 &&
              hasUnpostedOwnLine &&
              shortfallOf(landedUnits).missing > 0
            ) {
              mergedDoorQuantity = await this._mergedDoorQuantityInCart(
                configId
              );
            }
            if (mergedDoorQuantity > 0) {
              items.forEach((item) => {
                if (item.properties?._Configuration_ID !== configId) return;
                const credited =
                  (Number(item.quantity) || 0) * mergedDoorQuantity;
                landedUnits.set(
                  item.id,
                  (landedUnits.get(item.id) || 0) + credited
                );
              });
            }
            const { missing: missingUnits, shortIds: shortVariantIds } =
              shortfallOf(landedUnits);

            // Every dropped line is a unit shortfall too, so units alone decide.
            // Two posted lines Shopify merged into one (fewer lines, all units
            // present) are NOT short: that is the Save Cart guard's contract.
            if (missingUnits > 0) {
              const missingItems = items.filter((item) =>
                shortVariantIds.has(item.id)
              );

              isDev() &&
                console.error(
                  '[CartManager] Shopify silently dropped items from cart!',
                  {
                    expected: expectedCount,
                    actual: actualCount,
                    missingCount: missingItems.length,
                    missingUnits,
                    missingItems: missingItems.map((item) => ({
                      variantId: item.id,
                      category: item.properties?._Category,
                      title: item.properties?._Title,
                    })),
                  }
                );

              // PARTIAL-ADD ROLLBACK: the lines that DID land are removed
              // (only this attempt's, by line key) BEFORE FAILED is published,
              // so the button resets onto a clean cart and Try Again adds
              // exactly one copy. See known-footguns "A Partial Add Must Roll
              // Back Its Own Lines".
              return this._rollbackAttemptLines(configId, data.items).then(
                (rollback) => {
                  publishEvent(EVENTS.UI_HIDE_LOADING_SPINNER, {
                    source: 'cart-manager',
                    operation: 'add_to_cart_item_mismatch',
                  });

                  publishAtcFailedAnalytics('item_count_mismatch');
                  publishEvent(EVENTS.BUSINESS_CART_ADD_FAILED, {
                    source: 'cart-manager',
                    message:
                      rollback.status === 'failed'
                        ? `${missingUnits} item(s) could not be added, and some items from this attempt may still be in your cart. Please check your cart before trying again, or contact us for assistance.`
                        : `${missingUnits} item(s) could not be added, so nothing from this attempt was left in your cart. Please try again, or contact us for assistance.`,
                    error: `Cart item count mismatch: expected ${expectedCount}, got ${actualCount} (${missingUnits} unit(s) short)`,
                    missingItems: missingItems.map((item) => ({
                      variantId: item.id,
                      category: item.properties?._Category,
                      title: item.properties?._Title,
                    })),
                    rollback,
                    cartNeedsReview: rollback.status === 'failed',
                    timestamp: Date.now(),
                  });

                  return null; // Do NOT reveal the cart: show the error instead
                }
              );
            }

            // Update cart attributes with configuration data
            // Non-blocking: if this fails (e.g., rate limit), still proceed to cart

            // Add throttle delay between cart/add.js and cart/update.js
            return new Promise((resolve) =>
              setTimeout(resolve, CART_API_THROTTLE_MS)
            )
              .then(() => this.updateCartAttributes(configId, configDetails))
              .then(() => {
                isDev() &&
                  console.log(
                    '[CartManager] Cart attributes updated successfully'
                  );
                return data;
              })
              .catch((attrError) => {
                // Log the error but don't block cart redirect
                isDev() &&
                  console.warn(
                    '[CartManager] Cart attributes update failed (non-blocking):',
                    attrError.message
                  );

                if (attrError.status === 429) {
                  isDev() &&
                    console.warn(
                      '[CartManager] Rate limit hit on cart/update.js - proceeding to cart anyway'
                    );
                }

                // Return cart data so the operation continues
                return data;
              });
          }
        })
        .then((data) => {
          if (data) {
            isDev() &&
              console.log(
                '[CartManager] All items verified, redirecting to cart'
              );

            // PATTERN 12: Track performance
            const endTime = window.performance
              ? window.performance.now()
              : Date.now();
            const executionTime = endTime - startTime;

            trackPerformance(
              'ADD_TO_CART',
              executionTime,
              EVENTS.PERFORMANCE_CART_ADD_SLOW
            );

            // STAGE 4: SUCCESS
            addSucceeded = true;
            publishEvent(EVENTS.BUSINESS_CART_ADD_COMPLETED, {
              executionTime: executionTime.toFixed(2),
              itemCount: data.items?.length || 0,
              timestamp: Date.now(),
            });

            // Dual-publish add_to_cart to GTM (dataLayer) and Super App Web Pixel.
            // CCP modules don't use analytics-tracking.js's data-track-click delegation,
            // so this explicit publish ensures the configurator ATC is visible to Elevar,
            // GA4, Meta Pixel, TikTok, etc. — same payload shape as cart-drawer.js APO path.
            try {
              const _atcPayload = {
                element_action: 'add_to_cart',
                element_section: 'configurator',
                element_label: 'Add to Cart',
                element_position: 'primary_cta',
                page_type: document.body.dataset.pageType || '',
                product_id: document.body.dataset.productId || '',
              };
              // Staff traffic is never published (snippets/md-internal-traffic.liquid).
              if (window.MD_INTERNAL_TRAFFIC !== true) {
                if (window.dataLayer)
                  window.dataLayer.push({ event: 'element_click', ..._atcPayload });
                if (window.Shopify?.analytics?.publish)
                  window.Shopify.analytics.publish('md_element_click', _atcPayload);
              }
            } catch (_e) { /* analytics failure must never block the cart flow */ }

            // PATTERN 10: UI Update Events - Hide loading spinner on success
            publishEvent(EVENTS.UI_HIDE_LOADING_SPINNER, {
              source: 'cart-manager',
              operation: 'add_to_cart_success',
            });

            // CCP COMPLIANT: Publish cart updated event
            publishEvent(EVENTS.BUSINESS_CART_UPDATED, {
              source: 'configurator',
              cartData: data,
              sectionId,
              timestamp: Date.now(),
            });

            // CCP COMPLIANT: request the cart reveal via event. The UI layer
            // (ui-updater) owns the DOM decision — open the cart drawer when the
            // theme's cart type is Drawer, otherwise redirect to /cart. BL must
            // not touch the DOM (querySelector/dispatchEvent), so the surface
            // detection lives in the UI module. The add-to-cart analytics ping
            // already fired above, independent of which surface shows next.
            publishEvent(EVENTS.UI_CART_REVEAL_REQUESTED, {
              source: 'cart-manager',
              timestamp: Date.now(),
            });
          }
        })
        .catch(async (error) => {
          isDev() &&
            console.error('[CartManager] Cart add failed with error:', error);

          // A timed-out, dropped or unreadable add may still have LANDED
          // (Shopify processed it, the answer never arrived). Sweep this
          // attempt's lines by its unique _Configuration_ID before FAILED, so
          // Try Again never doubles a door. Nothing was posted by any other
          // path, so nothing else can match.
          const rollback =
            addSucceeded || addHttpStatus === 429
              ? { status: 'none', removedCount: 0 }
            : await this._rollbackAttemptLines(configId, null);
          // An aborted or unanswered add can still commit on Shopify's side
          // AFTER the sweep above read the cart. Keep watching this attempt's
          // id: re-sweep later on this page, and let cart.js sweep it if the
          // shopper moves on to the cart. Only this attempt's unique id.
          if (!addSucceeded && addHttpStatus !== 429) {
            this._watchForLateLanding(configId);
          }
          const rollbackFields = {
            rollback,
            cartNeedsReview: rollback.status === 'failed',
          };
          isDev() &&
            console.log('[CartManager] Error details:', {
              message: error.message,
              stack: error.stack,
              name: error.name,
            });

          // Always hide loading spinner on error
          publishEvent(EVENTS.UI_HIDE_LOADING_SPINNER, {
            source: 'cart-manager',
            operation: 'add_to_cart_error',
          });

          // A timed-out request aborts with a generic "AbortError" / "The
          // user aborted a request" message that tells the customer nothing.
          // Split into its own literal call + publish so it groups apart from
          // a real Shopify rejection instead of being lumped into
          // 'request_error' — a stalled mobile connection is a very different
          // support/monitoring signal than the cart API actually rejecting.
          if (error && error.name === 'AbortError') {
            publishAtcFailedAnalytics('request_timeout');
            // STAGE 5: FAILED
            publishEvent(EVENTS.BUSINESS_CART_ADD_FAILED, {
              source: 'cart-manager',
              message:
                'Adding your door to cart is taking longer than expected. Please check your connection and try again.',
              error: `Cart add timed out after ${CART_NETWORK_TIMEOUT_MS / 1000}s — request never completed`,
              ...rollbackFields,
              timestamp: Date.now(),
            });
            return;
          }

          publishAtcFailedAnalytics('request_error');
          // STAGE 5: FAILED
          publishEvent(EVENTS.BUSINESS_CART_ADD_FAILED, {
            source: 'cart-manager',
            message: 'Cart add failed',
            error: error.message,
            ...rollbackFields,
            timestamp: Date.now(),
          });
        });
    },

    // ============================================================================
    // PATTERN 3: 5-STAGE EVENT LIFECYCLE - Prepare Cart Items
    // PATTERN 7: PERFECT DICTIONARY ONLY (10 points)
    // PATTERN 8: NO RAW DATA ACCESS (10 points)
    // ============================================================================
    /**
     * Prepares cart items using PPD pre-built templates (O(1) lookups)
     *
     * Flow for each selection:
     * 1. Get PPD from conductor
     * 2. O(1) template lookup: ppd.cartItemTemplates.get(variantId)
     * 3. Clone template and add configuration-specific properties
     * 4. Zero async event round-trips - pure Map reads
     *
     * Performance: ~1-2ms for 5-10 selections (O(1) Map.get operations)
     */
    async prepareCartItems() {
      const startTime = window.performance
        ? window.performance.now()
        : Date.now();

      try {
        // STAGE 1: START
        publishEvent(EVENTS.BUSINESS_CART_PREPARATION_STARTED, {
          source: 'cart-manager.prepareCartItems',
          timestamp: Date.now(),
        });

        // PATTERN 7: Get PPD from conductor (O(1) access)
        const conductor = window.MurphyDoorConfigurator.conductor;
        if (!conductor) {
          throw new Error('Conductor not available - cannot access PPD');
        }

        const ppd = conductor.getPerfectProductDictionary();
        if (!ppd || !ppd.cartItemTemplates) {
          throw new Error('PPD cartItemTemplates not available');
        }

        const productData = this.getProductData();
        const items = [];
        const addOnEntries = [];
        const configId = this.generateConfigId();
        const selections = this._selections;

        // PATTERN 9: Request configuration URL via conductor delegation
        // instead of directly accessing persistenceManager (CCP compliance)
        const currentUrl = buildConfigurationUrl(conductor, selections);
        if (
          !conductor ||
          typeof conductor.serializeConfigurationState !== 'function'
        ) {
          // CCP COMPLIANT: No direct module access - use window.location.search default
          isDev() &&
            console.warn(
              '[CartManager] Conductor serializer unavailable, using window.location.search'
            );
        }

        isDev() &&
          console.log(
            '[CartManager] Starting O(1) PPD template-based cart preparation:',
            {
              selectionCount: Object.keys(selections.selections || selections)
                .length,
              templateCount: ppd.cartItemTemplates.size,
            }
          );

        // STAGE 2: VALIDATION + STAGE 3: PROGRESS
        // O(1) template reads instead of O(n) async lookups
        for (const [category, selection] of Object.entries(
          selections.selections || selections
        )) {
          const variantId = selection.variantId;

          if (!variantId) {
            throw new Error(
              `Cart preparation failed: Selection for category "${category}" is missing a variantId.`
            );
          }

          // DUAL-LOOKUP PATTERN: O(1) template lookup with CompositeProduct compatibility
          const template =
            ppd.cartItemTemplates.get(variantId) ||
            ppd.cartItemTemplates.get(parseInt(variantId));

          if (!template) {
            throw new Error(
              `Cart preparation failed: No cart item template found for variantId "${variantId}" in category "${category}". The PPD is incomplete.`
            );
          }

          // Check availability before adding to cart
          if (template.available === false) {
            throw new Error(
              `"${category}" is currently sold out. Please select a different option to continue.`
            );
          }

          isDev() &&
            console.log(`[CartManager] O(1) template lookup for ${category}`, {
              variantId,
              templateFound: true,
            });

          // PPD O(1) lookup for visible cart display value
          const variantObject =
            ppd.variantIdToVariantObjectMap?.get(variantId) ||
            ppd.variantIdToVariantObjectMap?.get(parseInt(variantId));
          const selectedValue =
            variantObject?.title || template.properties._Display_Name;

          // Clone template and add configuration-specific properties
          addOnEntries.push({
            category,
            item: {
              ...template,
              properties: {
                ...template.properties,
                [category]: selectedValue, // Visible in cart drawer/page (no underscore prefix)
                _Configuration_ID: configId,
                _Configuration_Time: Date.now(),
                _Add_on_for: productData.baseProduct?.title || 'Unknown Product',
              },
            },
          });
        }

        items.push(
          ...orderAddOnsForCart(addOnEntries, (category) =>
            conductor._getApoOrderRank(category)
          )
        );

        // FINISH CONSISTENCY CANARY: When simplifiedUxEnabled=true, all selected
        // add-on variants should share the same finish_sku. Detecting multiple
        // finish_skus here signals a cascade ordering regression — the sort-order
        // patch (commit 38226ad4) should prevent this in normal flow. This guard
        // does NOT block checkout; it publishes a monitoring event only.
        // See: mixed-finish-state bug post-mortem.
        if (ppd.simplifiedUxConfig?.enabled !== false) {
          const finishSkus = new Set();
          for (const item of items) {
            const vid = item.id || item.variantId;
            const varObj =
              ppd.variantIdToVariantObjectMap?.get(vid) ||
              ppd.variantIdToVariantObjectMap?.get(parseInt(vid));
            if (varObj?.finish_sku) {
              finishSkus.add(varObj.finish_sku);
            }
          }
          if (finishSkus.size > 1) {
            isDev() &&
              console.error(
                '[CartManager] FINISH CONSISTENCY CANARY: Mixed finish detected in cart items.',
                { finishSkus: Array.from(finishSkus) }
              );
            publishEvent(
              CONSTANTS.EVENTS.BUSINESS_CART_FINISH_INCONSISTENCY_DETECTED,
              {
                finishSkus: Array.from(finishSkus),
                source: 'cart-manager.prepareCartItems',
                timestamp: Date.now(),
              }
            );
            // NOT blocking checkout — this is a monitoring canary.
            // A BUSINESS_CART_FINISH_INCONSISTENCY_DETECTED event here signals
            // a regression in the cascade sort-order fix.
          }
        }

        // Add base product using PPD validation
        const baseValidation = ppd.baseProductValidation;

        if (baseValidation.error) {
          throw new Error(baseValidation.error.message);
        }

        const baseVariant = baseValidation.selectedVariant;
        const baseTemplate = ppd.cartItemTemplates.get(
          parseInt(baseVariant.id)
        );

        if (baseTemplate) {
          // Calculate configuration total (base + all addons)
          const configTotalInCents = items.reduce(
            (sum, item) => {
              const priceStr = String(item.properties._Price || '0').replace(
                /,/g,
                ''
              );
              const itemPriceDollars = parseFloat(priceStr) || 0;
              const itemPriceInCents = Math.round(itemPriceDollars * 100);
              const itemQuantity = parseInt(item.quantity, 10) || 1;
              return sum + itemPriceInCents * itemQuantity;
            },
            Math.round(
              (parseFloat(
                String(baseTemplate.properties._Price || '0').replace(/,/g, '')
              ) || 0) * 100
            )
          );

          isDev() &&
            console.log(
              '[CartManager] Adding base product via O(1) template lookup',
              {
                configTotalInCents,
                configTotal: (configTotalInCents / 100).toFixed(2),
              }
            );

          // Build config summary as a line item property so it reliably flows to OrderDesk.
          // NOTE: This intentionally duplicates data also stored in cart attributes (__config_* key).
          // Cart attributes with dynamic keys do not reliably transfer to orders in OrderDesk,
          // while line item properties are guaranteed to persist through order creation.
          const configSummary = JSON.stringify({
            baseProduct: {
              id: baseTemplate.id,
              title: productData.baseProduct?.title || 'Door',
              price: baseTemplate.properties._Price,
            },
            addOns: items.map((item) => ({
              id: item.id,
              category: item.properties._Category,
              title: item.properties._Display_Name,
              price: item.properties._Price,
            })),
            totalPrice: (configTotalInCents / 100).toFixed(2),
          });

          items.push({
            ...baseTemplate,
            properties: {
              ...baseTemplate.properties,
              [CONSTANTS.CART_VISIBLE_PROPERTY_NAMES.DOOR]:
                productData.baseProduct?.title ||
                baseTemplate.properties?._Display_Name ||
                'Door', // Visible in cart
              _Configuration_ID: configId,
              _Door_Title: productData.baseProduct?.title || 'Door',
              _Configuration_Time: Date.now(),
              _Configuration_URL: currentUrl,
              _Configuration_Total_Cents: configTotalInCents,
              _Config_Summary: configSummary,
              ...getBuildPresetLineProperties(conductor),
            },
          });
        } else {
          throw new Error(
            `Cart preparation failed: No cart item template found for base product variant ID "${baseVariant.id}". The PPD is incomplete.`
          );
        }

        // PATTERN 12: Track performance
        const endTime = window.performance
          ? window.performance.now()
          : Date.now();
        const executionTime = endTime - startTime;

        trackPerformance(
          'CART_PREPARATION',
          executionTime,
          EVENTS.PERFORMANCE_CART_PREPARATION_SLOW,
          {
            itemCount: items.length,
          }
        );

        // STAGE 4: SUCCESS
        publishEvent(EVENTS.BUSINESS_CART_PREPARATION_COMPLETED, {
          itemCount: items.length,
          executionTime: executionTime.toFixed(2),
          timestamp: Date.now(),
        });

        isDev() &&
          console.log(
            `[CartManager] Prepared ${items.length} cart items in ${executionTime.toFixed(2)}ms using PPD templates`
          );

        return items;
      } catch (error) {
        // STAGE 5: FAILED
        publishEvent(EVENTS.ERROR_CART_PREPARATION_FAILED, {
          error: error.message,
          timestamp: Date.now(),
        });
        isDev() &&
          console.error('[CartManager] prepareCartItems failed:', error);
        throw error;
      }
    },

    // Generates unique configuration ID for grouping cart items
    generateConfigId() {
      return `config_${Date.now()}_${Math.random().toString(36).substring(2, 11)}`;
    },

    // ============================================================================
    // PATTERN 3: 5-STAGE EVENT LIFECYCLE - Update Cart Attributes
    // PATTERN 14: TRY/CATCH + FAILED EVENTS (10 points)
    // ============================================================================
    /**
     * Updates cart attributes with configuration data to maintain relationships
     * between configured product items.
     *
     * @param {string} configId - Unique identifier for the configuration
     * @param {Object} configDetails - Configuration details to store as cart attributes
     * @returns {Promise<Object>} - Result of the cart attribute update operation
     */
    async updateCartAttributes(configId, configDetails) {
      const startTime = window.performance
        ? window.performance.now()
        : Date.now();

      try {
        // STAGE 2: VALIDATION
        if (!configId || typeof configId !== 'string') {
          throw new Error('Invalid configuration ID provided');
        }

        if (!configDetails || typeof configDetails !== 'object') {
          throw new Error('Invalid configuration details provided');
        }

        if (!configDetails.baseProduct || !configDetails.addOns) {
          throw new Error('Missing required configuration properties');
        }

        // STAGE 1: START
        publishEvent(EVENTS.BUSINESS_CART_ATTRIBUTES_UPDATE_STARTED, {
          configId,
          timestamp: Date.now(),
          source: 'cart-manager.updateCartAttributes',
        });

        const attributes = {
          [`__config_${configId}`]: JSON.stringify({
            baseProduct: configDetails.baseProduct,
            addOns: configDetails.addOns,
            timestamp: Date.now(),
            totalPrice: configDetails.totalPrice,
          }),
          __config_origin: configDetails.origin || 'full_configurator',
        };

        // STAGE 3: PROGRESS - 429 retry via the shared fetchWithRateLimitRetry
        // (same policy as /cart/add.js: only 429 retried, Retry-After honoured).
        const response = await fetchWithRateLimitRetry(async () => {
          // TIMEOUT GUARD: without this, a stalled connection here hangs the
          // whole add-to-cart chain forever — /cart/add.js already succeeded
          // (the item IS in the cart) but UI_CART_REVEAL_REQUESTED never
          // fires because it's published after this promise resolves, so the
          // drawer never opens and the spinner never clears. See
          // CART_NETWORK_TIMEOUT_MS above for the matching /cart/add.js fix.
          const updateController = new AbortController();
          const updateTimeoutId = setTimeout(
            () => updateController.abort(),
            CART_NETWORK_TIMEOUT_MS
          );
          try {
            return await fetch('/cart/update.js', {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
              },
              body: JSON.stringify({
                attributes: attributes,
              }),
              signal: updateController.signal,
            });
          } finally {
            clearTimeout(updateTimeoutId);
          }
        }, 'cart/update.js');

        if (response.ok) {
          const result = await response.json();

          // PATTERN 12: Performance tracking
          const endTime = window.performance
            ? window.performance.now()
            : Date.now();
          const executionTime = endTime - startTime;

          trackPerformance(
            'ATTRIBUTES_UPDATE',
            executionTime,
            EVENTS.PERFORMANCE_CART_ATTRIBUTES_UPDATE_SLOW
          );

          // STAGE 4: SUCCESS
          publishEvent(EVENTS.BUSINESS_CART_ATTRIBUTES_UPDATE_COMPLETED, {
            configId,
            executionTime: executionTime.toFixed(2),
            timestamp: Date.now(),
            source: 'cart-manager.updateCartAttributes',
          });

          // Best-effort hygiene, deliberately NOT awaited: the attribute
          // write above has already succeeded, so this must never delay the
          // cart reveal nor be able to fail it. See the method's own doc.
          void this.pruneOrphanedConfigAttributes(configId);

          return result;
        }

        // Rate limit persisted through every attempt, or a non-retryable
        // status: fail. status is kept so addToCart's .catch can tell a 429.
        const err = new Error(
          response.status === 429
            ? `Cart update rate limited (429) after ${CART_RATE_LIMIT_RETRY.MAX_ATTEMPTS} attempts`
            : `Cart update failed with status: ${response.status}`
        );
        err.status = response.status;
        throw err;
      } catch (error) {
        // STAGE 5: FAILED
        const endTime = window.performance
          ? window.performance.now()
          : Date.now();
        const executionTime = endTime - startTime;
        const isTimeout = error && error.name === 'AbortError';
        const errorMessage = isTimeout
          ? `Cart update timed out after ${CART_NETWORK_TIMEOUT_MS / 1000}s — request never completed`
          : error.message;

        publishEvent(EVENTS.BUSINESS_CART_ATTRIBUTES_UPDATE_FAILED, {
          error: errorMessage,
          configId,
          executionTime: executionTime.toFixed(2),
          timestamp: Date.now(),
          source: 'cart-manager.updateCartAttributes',
        });

        isDev() &&
          console.error('[CartManager] updateCartAttributes failed:', error);
        // Caller (.catch(attrError) in addToCart's promise chain) treats this
        // as non-blocking and proceeds with the cart reveal regardless — a
        // stalled attributes update must never leave the cart drawer closed
        // when the item itself was successfully added.
        throw error;
      }
    },

    /**
     * Garbage-collects ORPHANED configuration cart attributes.
     *
     * Every configurator save mints a FRESH configId and writes
     * `__config_<configId>`, but nothing ever removed the previous one — so a
     * shopper who reconfigures leaves the superseded blob behind, it rides the
     * cart into the order's note_attributes, and is never read again. Measured
     * on production 2026-09-21: 20 orphaned keys / 48,788 bytes across 14
     * orders and still accruing (order 98875 carried 2 orphans = 5,020 bytes).
     *
     * LOAD-BEARING DISTINCTION — a key whose configId STILL has a line item in
     * the cart must be KEPT. cart.js's updateQuantity is written to read
     * `__config_<configurationId>` on every quantity change of a configured
     * base product in order to re-sync the add-on lines. That particular read
     * is currently INERT (it goes through `fetchCartState()` → `/cart.js`,
     * which strips `__` keys — see the read lens below), and quantity re-sync
     * in fact survives via synchronizeConfigurationGroup, verified on both
     * staging and production. The guard is kept deliberately anyway: it is the
     * correct invariant, it costs nothing, and it is what stops this method
     * from destroying that data the moment that read is repaired.
     *
     * Conservative by construction — it can only ever UNDER-prune:
     *   - matches only the verified `__config_config_` shape, so the sibling
     *     `__config_origin` metadata key can never be caught by it;
     *   - never prunes the configId just written (during add-to-cart that
     *     configuration's line items are not in the cart yet, so it would
     *     otherwise look orphaned);
     *   - any failed or partial cart read prunes nothing at all.
     *
     * @param {string} keepConfigId - configuration just written; never pruned.
     * @returns {Promise<number>} attributes pruned (0 on any failure).
     */
    async pruneOrphanedConfigAttributes(keepConfigId) {
      const CONFIG_KEY_PREFIX = '__config_config_';
      const ATTR_PREFIX_LENGTH = '__config_'.length;
      try {
        const readController = new AbortController();
        const readTimeoutId = setTimeout(
          () => readController.abort(),
          CART_NETWORK_TIMEOUT_MS
        );
        let cart;
        try {
          // 🚨 DO NOT "simplify" this back to `GET /cart.js`.
          // Shopify STRIPS every cart attribute whose key starts with `__`
          // from the GET cart-JSON representation (`/cart.js` AND
          // `/cart.json`) — it is a PREFIX rule, so `x__config_G` survives
          // while `__config_config_A` does not. Since every key this method
          // exists to collect is `__config_`-prefixed, reading the GET makes
          // `attributes` permanently empty and the whole method a silent
          // no-op. Measured on production 2026-09-21: writing six mixed-prefix
          // keys echoed all six back from `/cart/update.js`, while `/cart.js`
          // returned only the three without the `__` prefix.
          // A no-op `POST /cart/update.js {attributes:{}}` echoes the FULL
          // cart — hidden attributes included — and its `items[].properties`
          // are byte-identical to `/cart.js`, so it is a drop-in read lens at
          // the same one-request cost.
          const response = await fetch('/cart/update.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ attributes: {} }),
            signal: readController.signal,
          });
          if (!response.ok) return 0;
          cart = await response.json();
        } finally {
          clearTimeout(readTimeoutId);
        }

        const attributes = (cart && cart.attributes) || {};

        // Configuration IDs that still have at least one line item in the cart.
        const liveConfigIds = new Set();
        for (const item of (cart && cart.items) || []) {
          const id =
            item && item.properties && item.properties._Configuration_ID;
          if (id) liveConfigIds.add(id);
        }

        const prune = {};
        let configKeysSeen = 0;
        for (const key of Object.keys(attributes)) {
          if (key.indexOf(CONFIG_KEY_PREFIX) !== 0) continue;
          configKeysSeen++;
          const configId = key.slice(ATTR_PREFIX_LENGTH);
          if (configId === keepConfigId) continue;
          if (liveConfigIds.has(configId)) continue;
          // Shopify removes a cart attribute written with an empty value.
          prune[key] = '';
        }

        // PRECONDITION ASSERT — the read lens below depends on UNDOCUMENTED
        // platform behaviour (see CLAUDE.md, "Shopify STRIPS __-Prefixed Cart
        // Attributes From GET /cart.js"), so it must be able to announce its own
        // failure instead of returning a 0 that looks like a clean cart.
        //
        // Our caller has just SUCCESSFULLY written `__config_<keepConfigId>`, so
        // the read MUST surface at least that one key. Zero config keys means the
        // read cannot see a write we know landed. That is the signature of Shopify
        // normalising /cart.js and /cart/update.js to the single response shape
        // their docs describe, which would silently make this method inert again —
        // the exact failure it was built to escape, and one the unit suite cannot
        // catch because its double models today's behaviour, not the contract.
        //
        // 🚨 This deliberately keys on the JUST-WRITTEN id, NOT on "the cart has
        // configured line items but no attributes". That weaker condition is a
        // LEGITIMATE state: the add-to-cart path treats updateCartAttributes as
        // non-blocking ("if this fails (e.g. rate limit), still proceed to cart"),
        // so a 429'd write leaves exactly that shape and would false-positive on
        // the very case the retry loop exists for.
        if (keepConfigId && configKeysSeen === 0) {
          // Always-on: console.warn is isDev-gated throughout this file, but this
          // is a platform-contract violation that must be visible in production
          // monitoring. It is unreachable while the platform behaves as measured,
          // so it cannot become dashboard noise.
          console.error(
            '[CartManager] Cart read surfaced ZERO __config_ attributes ' +
              'immediately after one was written successfully — hidden cart ' +
              'attributes are no longer readable and orphan collection is INERT. ' +
              'See CLAUDE.md: "Shopify STRIPS __-Prefixed Cart Attributes From ' +
              'GET /cart.js".'
          );
          return 0;
        }

        const pruneCount = Object.keys(prune).length;
        if (pruneCount === 0) return 0;

        const writeController = new AbortController();
        const writeTimeoutId = setTimeout(
          () => writeController.abort(),
          CART_NETWORK_TIMEOUT_MS
        );
        try {
          const writeResponse = await fetch('/cart/update.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ attributes: prune }),
            signal: writeController.signal,
          });
          if (!writeResponse.ok) return 0;
        } finally {
          clearTimeout(writeTimeoutId);
        }

        isDev() &&
          console.log(
            `[CartManager] Pruned ${pruneCount} orphaned configuration attribute(s)`
          );
        return pruneCount;
      } catch (_error) {
        // Best-effort hygiene only — never surface, never retry.
        return 0;
      }
    },

    // ============================================================================
    // PARTIAL-ADD ROLLBACK (PATTERN 14: try/catch, never throws)
    // ============================================================================
    /**
     * Removes the lines ONE add attempt put in the cart, and nothing else.
     *
     * Identity is the attempt's `_Configuration_ID`: prepareCartItems() mints a
     * fresh one per attempt and every line of the door carries it, so it names
     * this attempt's lines exactly. Removal is by line `key` (quantity 0 in one
     * POST /cart/update.js). NEVER by variant id: another door, or the same
     * door added earlier, shares variants and must survive.
     *
     * @param {string} configId - this attempt's _Configuration_ID
     * @param {Array|null} landedLines - the /cart/add.js response `items`, or
     *   null when the add's answer never arrived (then /cart.js is read)
     * @returns {Promise<{status: 'none'|'rolled_back'|'failed', removedCount: number, error?: string}>}
     *   'none': nothing from this attempt is in the cart. 'rolled_back': it was
     *   all removed and the cart was re-checked. 'failed': it may still be
     *   there, so the shopper must be told to check the cart.
     */
    /**
     * Doors of this attempt that /cart.js shows MERGED by the Cart Transform
     * (has_components lines carrying configId; see mergedDoorQuantityFor).
     * The add.js response omits has_components, so this is the one read that
     * can tell a merged landing from a short one. Never throws: an unreadable
     * cart answers 0, which keeps today's behaviour (the partial-add rollback).
     * @param {string} configId
     * @returns {Promise<number>}
     */
    async _mergedDoorQuantityInCart(configId) {
      try {
        const response = await fetchWithRateLimitRetry(() => {
          const controller = new AbortController();
          const timeoutId = setTimeout(
            () => controller.abort(),
            CART_NETWORK_TIMEOUT_MS
          );
          return fetch('/cart.js', {
            headers: { Accept: 'application/json' },
            signal: controller.signal,
          }).finally(() => clearTimeout(timeoutId));
        }, 'cart.js (merged door check)');
        if (!response.ok) return 0;
        const cart = await response.json();
        return mergedDoorQuantityFor(cart && cart.items, configId);
      } catch (error) {
        isDev() &&
          console.warn('[CartManager] Merged door check failed:', error);
        return 0;
      }
    },

    async _rollbackAttemptLines(configId, landedLines) {
      const rollback = await this._removeAttemptLines(configId, landedLines);
      publishRollbackAnalytics(rollback, configId);
      return rollback;
    },

    /** Overridable for tests; production uses LATE_SWEEP_DELAYS_MS. */
    lateSweepDelaysMs: null,

    /** Overridable for tests; production uses CONFIGURED_DOOR_SNAPSHOT_WAIT_MS. */
    configuredDoorSnapshotWaitMs: null,

    /** Overridable for tests; production uses CONFIGURED_DOOR_IMAGE_ADD_WAIT_MS. */
    configuredDoorImageAddWaitMs: null,

    /** state key -> Promise<{url, reason}>: ONE upload per configuration state. */
    _doorImagePrefetches: null,

    /** Uploads started on this page (CONFIGURED_DOOR_PREFETCH_MAX_UPLOADS cap). */
    _doorImageUploadCount: 0,

    /**
     * The current configuration's state key, from the same builder as the
     * add's _Configuration_URL. '' when selections are not loaded (they are
     * dropped on every selection change, so '' also means "changed").
     * @returns {string}
     */
    _currentConfigurationStateKey() {
      if (!this._selections) return '';
      return configurationStateKey(
        buildConfigurationUrl(
          window.MurphyDoorConfigurator?.conductor,
          this._selections
        )
      );
    },

    /**
     * Pre-click upload of the configured-door picture for the CURRENT
     * configuration (Gap Plan #25b). Keyed by the configuration state: an
     * unchanged configuration never re-uploads, and concurrent requests for
     * one state share ONE in-flight promise. The snapshot is requested with
     * `purpose: 'prefetch'` (not stored, not a reveal gate). If the
     * configuration changes while the snapshot is taken the result is
     * discarded, so a picture is only ever cached under the state it shows.
     * Never rejects; resolves null when no prefetch is started.
     *
     * @returns {Promise<{url: string|null, reason: string}|null>}
     */
    _prefetchConfiguredDoorImage() {
      if (
        this._addInFlightSince &&
        Date.now() - this._addInFlightSince < ADD_IN_FLIGHT_STALE_MS
      ) {
        return Promise.resolve(null);
      }
      return new Promise((resolve) => this.requestSelections(resolve))
        .then(() => {
          const key = this._currentConfigurationStateKey();
          if (!key) return null;
          if (!this._doorImagePrefetches) this._doorImagePrefetches = new Map();
          const cache = this._doorImagePrefetches;
          if (cache.has(key)) return cache.get(key);

          const token = `config_${Date.now()}_${key}`;
          const pending = this._awaitCartSnapshot(token)
            .then((blob) => {
              if (!blob) return { url: null, reason: 'no_snapshot' };
              if (this._currentConfigurationStateKey() !== key) {
                return { url: null, reason: 'state_changed' };
              }
              const store = window.MDConfigurationImageStore;
              if (!store || typeof store.upload !== 'function') {
                return { url: null, reason: 'no_uploader' };
              }
              if (this._doorImageUploadCount >= CONFIGURED_DOOR_PREFETCH_MAX_UPLOADS) {
                return { url: null, reason: 'upload_cap' };
              }
              this._doorImageUploadCount += 1;
              return store.upload(token, blob);
            })
            .catch(() => ({ url: null, reason: 'error' }))
            .then((result) => {
              // Nothing was uploaded for this state: let a later trigger try
              // again. An answered upload (even a failed one) stays cached, so
              // one state never costs more than one upload.
              if (
                result.reason === 'no_snapshot' ||
                result.reason === 'state_changed' ||
                result.reason === 'upload_cap'
              ) {
                if (cache.get(key) === pending) cache.delete(key);
              }
              return result;
            });
          cache.set(key, pending);
          publishEvent(EVENTS.UI_CART_IMAGE_SNAPSHOT_REQUESTED, {
            source: 'cart-manager',
            configId: token,
            purpose: 'prefetch',
            timestamp: Date.now(),
          });
          return pending;
        })
        .catch(() => null);
    },

    /**
     * The prefetched image URL for the door being added, keyed by its BASE
     * line's _Configuration_URL. Resolves immediately when there is no
     * prefetch for that state or it is already settled, and NEVER waits longer
     * than CONFIGURED_DOOR_IMAGE_ADD_WAIT_MS for one in flight. Never rejects.
     *
     * @param {Object|null} baseItem - the payload's configurator base line
     * @returns {Promise<{url: string|null, reason: string}>}
     */
    async _configuredDoorImageForAdd(baseItem) {
      const key = configurationStateKey(
        baseItem && baseItem.properties
          ? baseItem.properties._Configuration_URL
          : ''
      );
      const pending =
        key && this._doorImagePrefetches
          ? this._doorImagePrefetches.get(key)
          : null;
      if (!pending) return { url: null, reason: 'no_prefetch' };
      let timer = null;
      const cap = new Promise((resolve) => {
        timer = setTimeout(
          () => resolve({ url: null, reason: 'not_ready' }),
          this.configuredDoorImageAddWaitMs ?? CONFIGURED_DOOR_IMAGE_ADD_WAIT_MS
        );
      });
      try {
        const result = await Promise.race([pending, cap]);
        return result && result.url
          ? result
          : { url: null, reason: (result && result.reason) || 'no_url' };
      } catch (_error) {
        return { url: null, reason: 'error' };
      } finally {
        clearTimeout(timer);
      }
    },

    /**
     * Resolves with this attempt's snapshot blob, or null when the snapshot
     * failed or did not arrive within the wait. Never rejects.
     *
     * @param {string} configId
     * @returns {Promise<Blob|null>}
     */
    _awaitCartSnapshot(configId) {
      return new Promise((resolve) => {
        let settled = false;
        let timer = null;
        const unsubscribers = [];
        const finish = (blob) => {
          if (settled) return;
          settled = true;
          clearTimeout(timer);
          unsubscribers.forEach((unsubscribe) => {
            if (typeof unsubscribe === 'function') unsubscribe();
          });
          resolve(blob && typeof blob.arrayBuffer === 'function' ? blob : null);
        };
        timer = setTimeout(
          () => finish(null),
          this.configuredDoorSnapshotWaitMs ?? CONFIGURED_DOOR_SNAPSHOT_WAIT_MS
        );
        if (!configId || !CoreEventBus) {
          finish(null);
          return;
        }
        if (CoreEventBus && typeof CoreEventBus.subscribe === 'function') {
          unsubscribers.push(
            CoreEventBus.subscribe(
              EVENTS.UI_CART_IMAGE_SNAPSHOT_COMPLETED,
              (data) => {
                if (data && data.configId === configId) finish(data.blob);
              }
            )
          );
        }
        if (CoreEventBus && typeof CoreEventBus.subscribe === 'function') {
          unsubscribers.push(
            CoreEventBus.subscribe(EVENTS.UI_CART_IMAGE_SNAPSHOT_FAILED, (data) => {
              if (data && data.configId === configId) finish(null);
            })
          );
        }
      });
    },


    /**
     * Late-landing guard for a failed add whose request may still commit
     * (2026-10-07). Records the attempt's _Configuration_ID in sessionStorage
     * (`mdFailedConfigIds`, swept by cart.js mdSweepFailedAttempts on the cart
     * page and drawer) and re-sweeps on this page at LATE_SWEEP_DELAYS_MS. A
     * re-sweep that finds nothing is silent; one that removes lines reports
     * add_to_cart_rollback `late_rolled_back|removed_<N>`. Never throws.
     *
     * @param {string} configId - the failed attempt's _Configuration_ID
     */
    _watchForLateLanding(configId) {
      if (!configId) return;
      try {
        const key = 'mdFailedConfigIds';
        const ids = JSON.parse(sessionStorage.getItem(key) || '{}') || {};
        ids[configId] = Date.now();
        sessionStorage.setItem(key, JSON.stringify(ids));
      } catch (_e) {
        /* storage unavailable (private mode): the in-page re-sweeps still run */
      }
      (this.lateSweepDelaysMs || LATE_SWEEP_DELAYS_MS).forEach((delay) => {
        setTimeout(async () => {
          const late = await this._removeAttemptLines(configId, null);
          if (late.removedCount > 0) {
            publishRollbackAnalytics(
              { status: 'late_rolled_back', removedCount: late.removedCount },
              configId
            );
            const cartItems =
              document.querySelector('cart-drawer-items') ||
              document.querySelector('cart-items');
            if (cartItems && typeof cartItems.onCartUpdate === 'function') {
              cartItems.onCartUpdate();
            }
          }
        }, delay);
      });
    },

    /** The removal itself; _rollbackAttemptLines adds the analytics. Never throws. */
    async _removeAttemptLines(configId, landedLines) {
      const isOwnLine = (line) =>
        !!(
          line &&
          line.key &&
          line.properties &&
          line.properties._Configuration_ID === configId
        );
      // One request per attempt with its OWN timeout (fetchWithRateLimitRetry
      // contract): a rollback that stalls must still end in FAILED.
      const timedFetch = (url, init) => {
        const controller = new AbortController();
        const timeoutId = setTimeout(
          () => controller.abort(),
          CART_NETWORK_TIMEOUT_MS
        );
        return fetch(url, { ...init, signal: controller.signal }).finally(() =>
          clearTimeout(timeoutId)
        );
      };

      if (!configId) {
        return {
          status: 'failed',
          removedCount: 0,
          error: 'No configuration id to roll back by',
        };
      }

      try {
        let lines = Array.isArray(landedLines) ? landedLines : null;
        // The add response lists only this request's lines. If any of them
        // does not carry our id (properties stripped), it cannot be trusted
        // as a map, so read the cart instead.
        if (!lines || !lines.every(isOwnLine)) {
          const readResponse = await fetchWithRateLimitRetry(
            () =>
              timedFetch('/cart.js', {
                headers: { Accept: 'application/json' },
              }),
            'cart.js (rollback read)'
          );
          if (!readResponse.ok) {
            throw new Error(`Cart read failed: HTTP ${readResponse.status}`);
          }
          const cart = await readResponse.json();
          lines = (cart && cart.items) || [];
        }

        const keys = [...new Set(lines.filter(isOwnLine).map((l) => l.key))];
        if (keys.length === 0) return { status: 'none', removedCount: 0 };

        const updates = {};
        keys.forEach((key) => {
          updates[key] = 0;
        });

        const writeResponse = await fetchWithRateLimitRetry(
          () =>
            timedFetch('/cart/update.js', {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                Accept: 'application/json',
              },
              body: JSON.stringify({ updates }),
            }),
          'cart/update.js (rollback)'
        );
        if (!writeResponse.ok) {
          throw new Error(`Rollback failed: HTTP ${writeResponse.status}`);
        }

        // The update answers with the whole cart: prove none of ours is left.
        const after = await writeResponse.json();
        const left = ((after && after.items) || []).filter(
          (line) =>
            line &&
            line.properties &&
            line.properties._Configuration_ID === configId
        );
        if (!after || !Array.isArray(after.items) || left.length > 0) {
          throw new Error(
            `Rollback left ${left.length} line(s) of ${configId} in the cart`
          );
        }

        isDev() &&
          console.log(
            `[CartManager] Rolled back ${keys.length} line(s) of a failed add`,
            { configId }
          );
        return { status: 'rolled_back', removedCount: keys.length };
      } catch (error) {
        isDev() &&
          console.error('[CartManager] Partial-add rollback failed:', error);
        return {
          status: 'failed',
          removedCount: 0,
          error: (error && error.message) || String(error),
        };
      }
    },

    // ============================================================================
    // PATTERN 3: 5-STAGE EVENT LIFECYCLE - Calculate Total Price
    // ============================================================================
    async calculateTotalPrice() {
      const startTime = window.performance
        ? window.performance.now()
        : Date.now();

      try {
        // STAGE 1: START
        publishEvent(EVENTS.BUSINESS_CART_PRICE_CALC_STARTED, {
          source: 'cart-manager.calculateTotalPrice',
          timestamp: Date.now(),
        });

        isDev() &&
          console.log('[CartManager] Requesting total price calculation');

        // PATTERN 9: Data request via event system
        const response = await this._requestAsyncData(
          EVENTS.PRICE_REQUEST_TOTAL_CALCULATION,
          EVENTS.PRICE_CALCULATION_RESPONSE,
          { source: 'cart-manager' },
          'Price calculation request timed out after 5 seconds'
        );

        if (!response || typeof response.totalPrice === 'undefined') {
          throw new Error('Invalid price calculation response from conductor');
        }

        // PATTERN 12: Performance tracking
        const endTime = window.performance
          ? window.performance.now()
          : Date.now();
        const executionTime = endTime - startTime;

        trackPerformance(
          'PRICE_CALCULATION',
          executionTime,
          EVENTS.PERFORMANCE_CART_PRICE_CALC_SLOW
        );

        // STAGE 4: SUCCESS
        publishEvent(EVENTS.BUSINESS_CART_PRICE_CALC_COMPLETED, {
          totalPrice: response.totalPrice,
          executionTime: executionTime.toFixed(2),
          timestamp: Date.now(),
          source: 'cart-manager.calculateTotalPrice',
        });

        isDev() &&
          console.log(
            '[CartManager] Received total price:',
            response.totalPrice
          );
        return response.totalPrice;
      } catch (error) {
        // STAGE 5: FAILED
        publishEvent(EVENTS.BUSINESS_CART_PRICE_CALC_FAILED, {
          error: error.message,
          source: 'cart-manager.calculateTotalPrice',
          timestamp: Date.now(),
        });

        isDev() &&
          console.error('[CartManager] Price calculation failed:', error);
        throw error;
      }
    },

    async getConfigurationGroups() {
      const response = await fetch('/cart.js');
      const cart = await response.json();

      const configs = {};
      Object.entries(cart.attributes)
        .filter(([key]) => key.startsWith('__config_'))
        .forEach(([key, value]) => {
          configs[key.replace('__config_', '')] = JSON.parse(value);
        });

      return configs;
    },

    // PATTERN 10: UI UPDATE EVENTS - Request UI sections via event bus
    getSectionsToRender() {
      publishEvent(EVENTS.UI_REQUEST_CART_SECTIONS, {
        source: 'cart-manager',
        timestamp: Date.now(),
      });

      // Return default sections structure (will be enhanced by ui-updater response)
      return [
        {
          id: 'main-cart-items',
          section: 'main-cart-items',
          selector: '.js-contents',
        },
        {
          id: 'cart-icon-bubble',
          section: 'cart-icon-bubble',
          selector: '.shopify-section',
        },
        {
          id: 'cart-live-region-text',
          section: 'cart-live-region-text',
          selector: '.shopify-section',
        },
        {
          id: 'main-cart-footer',
          section: 'main-cart-footer',
          selector: '.js-contents',
        },
      ];
    },

    // PATTERN 10: UI UPDATE EVENTS - Request UI update via event bus
    updateCartUI() {
      publishEvent(EVENTS.UI_UPDATE_CART_UI, {
        source: 'cart-manager',
        timestamp: Date.now(),
      });
    },

    // ============================================================================
    // PATTERN 15: CLEANUP ARRAYS (10 points)
    // ============================================================================
    cleanup() {
      cleanupHandlers.forEach((unsub) => {
        if (typeof unsub === 'function') unsub();
      });
      cleanupHandlers = [];
      isInitialized = false;

      publishEvent(EVENTS.MODULE_CART_MANAGER_CLEANUP, {
        module: 'cartManager',
        timestamp: Date.now(),
      });

      isDev() &&
        console.log(
          '[CartManager] Cleanup complete - all subscriptions removed'
        );
    },
  };

  // Initialize cart manager event subscriptions immediately at module load time
  // Bootstrap barrier handles race protection
  const _cartMgr = window.MurphyDoorConfigurator.cartManager;
  _cartMgr.initialize();

  isDev() && console.log('[CartManager] Module loaded');
})(window);
